/**
 * Audio Recorder Module
 *
 * Handles longform audio recording with:
 * - MediaRecorder with chunked persistence to IndexedDB
 * - Wake Lock API + NoSleep fallback for device sleep prevention
 * - Upload to server for diarization/transcription processing
 * - SSE progress tracking during processing
 * - KB picker and speaker rename post-processing
 */

import { getHeaders } from './api.js';
import { API_BASE, state } from './state.js';
import { confirm as confirmDialog } from './confirm-dialog.js';
import { showToast } from './toast.js';

// ── Constants ────────────────────────────────────────────────────────────────

const DB_NAME = 'ragsmith_recordings';
const DB_VERSION = 1;
const STORE_NAME = 'chunks';
const CHUNK_INTERVAL_MS = 30000; // Save chunk every 30 seconds
const MIME_TYPE = 'audio/webm;codecs=opus';

// ── State ────────────────────────────────────────────────────────────────────

let mediaRecorder = null;
let audioStream = null;
let audioContext = null;
let analyserNode = null;
let recordingId = null;
let displayStream = null;  // system audio via getDisplayMedia
let mixDestination = null; // Web Audio mix destination node
let micGainNode = null;    // Gain on the mic→mix path so Mute only silences the mic
let chunkIndex = 0;
let startTime = 0;
let pausedDuration = 0;
let pauseStart = 0;
let timerInterval = null;
let animationFrame = null;
let wakeLock = null;
let noSleepAudio = null;
let noSleepVideo = null;
let db = null;
let recordingState = 'idle'; // idle, recording, paused, stopped, uploading, processing
let processingEventSource = null;
let processingAbortController = null; // For stopping processing mid-stream
let lastUploadedPath = null;   // Server path after successful upload — survives errors for retry
let lastChunkWrite = null;     // Promise of the most recent IndexedDB chunk write (stop-race guard)
let lastKbName = null;
let lastHfToken = null;
// The chip choices of the last run, so Retry repeats what was asked for
// instead of falling back to the defaults (which switch every stage on).
let lastProcArgs = null;
// Stages the current run was asked to do. A skipped stage still reports
// itself as done, and labelling it would show work that never happened.
let requestedStages = null;
let isMuted = false;           // Mic mute state (mic kept open, tracks disabled)
let silenceMs = 0;             // Consecutive silence accumulator (ms)
let lastSilenceCheck = 0;      // Timestamp of last silence sample
let silenceTriggered = false;  // True once silence auto-stop has fired (per recording)
let autouploadTimer = null;    // setInterval handle for the post-stop countdown
let autouploadRemaining = 0;   // Seconds left in the countdown

// Silence detection: 5 minutes of consecutive low RMS triggers auto-stop.
// Threshold is on a 0-1 scale of normalized analyser frequency data —
// 0.02 corresponds to roughly -34 dBFS, well below normal speech levels.
const SILENCE_THRESHOLD = 0.02;
const SILENCE_DURATION_MS = 5 * 60 * 1000;
const AUTOUPLOAD_COUNTDOWN_SEC = 5 * 60;

// ── IndexedDB ────────────────────────────────────────────────────────────────

function openDB() {
    return new Promise((resolve, reject) => {
        if (db) { resolve(db); return; }
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = (e) => {
            const store = e.target.result.createObjectStore(STORE_NAME, { keyPath: ['recordingId', 'chunkIndex'] });
            store.createIndex('byRecording', 'recordingId');
        };
        req.onsuccess = (e) => { db = e.target.result; resolve(db); };
        req.onerror = (e) => reject(e.target.error);
    });
}

async function saveChunk(recId, idx, blob, duration, status) {
    const d = await openDB();
    return new Promise((resolve, reject) => {
        const tx = d.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).put({
            recordingId: recId,
            chunkIndex: idx,
            blob,
            timestamp: Date.now(),
            totalDuration: duration,
            status,
        });
        tx.oncomplete = resolve;
        tx.onerror = () => reject(tx.error);
    });
}

async function getChunks(recId) {
    const d = await openDB();
    return new Promise((resolve, reject) => {
        const tx = d.transaction(STORE_NAME, 'readonly');
        const index = tx.objectStore(STORE_NAME).index('byRecording');
        const req = index.getAll(recId);
        req.onsuccess = () => resolve(req.result.sort((a, b) => a.chunkIndex - b.chunkIndex));
        req.onerror = () => reject(req.error);
    });
}

async function clearChunks(recId) {
    const d = await openDB();
    const chunks = await getChunks(recId);
    return new Promise((resolve, reject) => {
        const tx = d.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        for (const chunk of chunks) {
            store.delete([chunk.recordingId, chunk.chunkIndex]);
        }
        tx.oncomplete = resolve;
        tx.onerror = () => reject(tx.error);
    });
}

// NOTE: the old findIncompleteRecording() helper (scan ALL chunks, prompt
// for any recording whose last chunk isn't status='complete') was removed.
// Recovery is now driven by the explicit ACTIVE_RECORDING_KEY localStorage
// marker — see checkForIncompleteRecording(). Orphaned chunks without a
// marker surface in the Audio Manager as Recovered Recordings instead.

// ── Wake Lock ────────────────────────────────────────────────────────────────

async function acquireWakeLock() {
    // Primary: Wake Lock API
    if ('wakeLock' in navigator) {
        try {
            wakeLock = await navigator.wakeLock.request('screen');
            wakeLock.addEventListener('release', () => { wakeLock = null; });
            // Re-acquire on visibility change
            document.addEventListener('visibilitychange', reacquireWakeLock);
            return;
        } catch (e) {
            console.warn('Wake Lock API failed, using fallback:', e);
        }
    }

    // Fallback: NoSleep pattern (silent audio + invisible video)
    noSleepFallback();
}

async function reacquireWakeLock() {
    if (document.visibilityState === 'visible' && recordingState === 'recording') {
        if ('wakeLock' in navigator && !wakeLock) {
            try { wakeLock = await navigator.wakeLock.request('screen'); } catch (e) { /* ignore */ }
        }
    }
}

function noSleepFallback() {
    // Silent audio loop
    try {
        // Minimal silent WAV (44 bytes header + 1 sample)
        const silence = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
        noSleepAudio = new Audio(silence);
        noSleepAudio.loop = true;
        noSleepAudio.volume = 0.001;
        noSleepAudio.play().catch(() => {});
    } catch (e) { /* ignore */ }

    // Invisible video (iOS keeps screen on when playing video)
    try {
        noSleepVideo = document.createElement('video');
        noSleepVideo.setAttribute('playsinline', '');
        noSleepVideo.setAttribute('muted', '');
        noSleepVideo.style.cssText = 'position:fixed;top:-1px;left:-1px;width:1px;height:1px;opacity:0.01';
        // Use a data URI for a tiny blank MP4 — works on iOS Safari
        noSleepVideo.src = 'data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZQAAA' +
            'ABtZGF0AAAAMGJIQiAQNRAoIBgJEAAAggAAACAlBh4AAAAEaESNIAAAAARo8g8gAAAADGhCIBAORA==';
        document.body.appendChild(noSleepVideo);
        noSleepVideo.play().catch(() => {});
    } catch (e) { /* ignore */ }
}

function releaseWakeLock() {
    document.removeEventListener('visibilitychange', reacquireWakeLock);
    if (wakeLock) { wakeLock.release().catch(() => {}); wakeLock = null; }
    if (noSleepAudio) { noSleepAudio.pause(); noSleepAudio = null; }
    if (noSleepVideo) { noSleepVideo.pause(); noSleepVideo.remove(); noSleepVideo = null; }
}

// ── Elapsed Timer ────────────────────────────────────────────────────────────

function getElapsed() {
    if (recordingState === 'paused') return pausedDuration;
    if (startTime === 0) return 0;
    return pausedDuration + (Date.now() - startTime) / 1000;
}

function formatTime(seconds) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function startTimer() {
    const timerEl = document.getElementById('rec-timer');
    if (!timerEl) return;
    timerInterval = setInterval(() => {
        timerEl.textContent = formatTime(getElapsed());
    }, 500);
}

function stopTimer() {
    if (timerInterval) { clearInterval(timerInterval); timerInterval = null; }
}

// ── Level Meter (7-bar) ─────────────────────────────────────────────────────

function startLevelMeter() {
    const bars = document.querySelectorAll('.rec-level-bar');
    if (!bars.length || !analyserNode) return;

    const data = new Uint8Array(analyserNode.frequencyBinCount);
    const barCount = bars.length;
    const silenceEnabled = document.getElementById('rec-silence-stop')?.checked !== false;
    lastSilenceCheck = performance.now();

    function update() {
        analyserNode.getByteFrequencyData(data);
        // Sample across frequency range, weighted toward lower frequencies
        const step = Math.max(1, Math.floor(data.length / (barCount * 2)));
        let avg = 0;
        for (let i = 0; i < barCount; i++) {
            const idx = Math.min(i * step, data.length - 1);
            const val = data[idx] / 255;
            avg += val;
            const height = Math.max(4, val * 60); // 4px min, 60px max
            bars[i].style.height = `${height}px`;
        }
        avg /= barCount;

        // Silence detection — only count silence while actively recording and unmuted.
        // Muted state would always be silent which would falsely trigger auto-stop.
        if (silenceEnabled && !silenceTriggered && recordingState === 'recording' && !isMuted) {
            const now = performance.now();
            const dt = now - lastSilenceCheck;
            lastSilenceCheck = now;
            if (avg < SILENCE_THRESHOLD) {
                silenceMs += dt;
                if (silenceMs >= SILENCE_DURATION_MS) {
                    silenceTriggered = true;
                    onSilenceAutoStop();
                }
            } else {
                silenceMs = 0;
            }
        } else {
            lastSilenceCheck = performance.now();
        }

        animationFrame = requestAnimationFrame(update);
    }
    update();
}

function stopLevelMeter() {
    if (animationFrame) { cancelAnimationFrame(animationFrame); animationFrame = null; }
    document.querySelectorAll('.rec-level-bar').forEach(b => b.style.height = '4px');
}

// ── Mute / Silence Auto-stop ────────────────────────────────────────────────

function applyMute(muted) {
    isMuted = muted;
    if (micGainNode) {
        // Shared-audio mix mode: silence only the mic path in the Web Audio graph
        // so system audio in the same mix keeps flowing.
        micGainNode.gain.value = muted ? 0 : 1;
    } else if (audioStream) {
        // Mic-only mode: no mix to worry about — disable the track directly.
        audioStream.getAudioTracks().forEach(t => { t.enabled = !muted; });
    }
    // Reset silence accumulator when mute toggles so unmute doesn't carry over
    // accrued silence-time from the muted period.
    silenceMs = 0;
    lastSilenceCheck = performance.now();

    const overlay = document.getElementById('recording-overlay');
    overlay?.classList.toggle('muted', muted);
    const iconOn = document.getElementById('rec-mute-icon-on');
    const iconOff = document.getElementById('rec-mute-icon-off');
    const label = document.getElementById('rec-mute-label');
    const btn = document.getElementById('rec-mute-btn');
    if (iconOn) iconOn.style.display = muted ? 'none' : '';
    if (iconOff) iconOff.style.display = muted ? '' : 'none';
    if (label) label.textContent = muted ? 'Unmute' : 'Mute';
    if (btn) {
        btn.classList.toggle('active', muted);
        btn.title = muted ? 'Unmute mic' : 'Mute mic';
    }
}

export function toggleMute() {
    if (recordingState !== 'recording' && recordingState !== 'paused') return;
    applyMute(!isMuted);
}

function onSilenceAutoStop() {
    // Fire auto-stop, then show the upload-only countdown.
    // stopRecording() switches state to 'stopped' which surfaces the
    // complete overlay; we then show the countdown overlay on top of it.
    stopRecording().then(() => {
        startAutoUploadCountdown();
    });
}

function startAutoUploadCountdown() {
    const overlay = document.getElementById('recording-autoupload-overlay');
    const display = document.getElementById('rec-autoupload-countdown');
    if (!overlay || !display) return;

    autouploadRemaining = AUTOUPLOAD_COUNTDOWN_SEC;
    const fmt = () => {
        const m = Math.floor(autouploadRemaining / 60);
        const s = autouploadRemaining % 60;
        display.textContent = `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
    };
    fmt();
    overlay.classList.add('active');

    if (autouploadTimer) clearInterval(autouploadTimer);
    autouploadTimer = setInterval(() => {
        autouploadRemaining -= 1;
        if (autouploadRemaining <= 0) {
            triggerAutoUpload();
            return;
        }
        fmt();
    }, 1000);
}

function cancelAutoUploadCountdown() {
    if (autouploadTimer) { clearInterval(autouploadTimer); autouploadTimer = null; }
    document.getElementById('recording-autoupload-overlay')?.classList.remove('active');
}

function triggerAutoUpload() {
    cancelAutoUploadCountdown();
    // Use a timestamp-based default KB name and run the Upload Only flow.
    // This matches the user-facing "Upload Only" chip in the preprocess dialog.
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    uploadOnlyFlow(`auto-${stamp}`, null, null);
}

// ── UI State Management ─────────────────────────────────────────────────────

function setRecordingState(newState) {
    recordingState = newState;
    const overlay = document.getElementById('recording-overlay');
    const completeOverlay = document.getElementById('recording-complete-overlay');
    const processingOverlay = document.getElementById('recording-processing-overlay');
    const chatInput = document.getElementById('user-input');
    const sendBtn = document.getElementById('send-btn');
    const plusBtn = document.getElementById('plus-menu-btn');
    const voiceBtn = document.getElementById('voice-mode-btn');

    // Disable/enable chat controls based on recording state
    const isActive = newState !== 'idle';
    if (chatInput) chatInput.disabled = isActive;
    if (sendBtn) sendBtn.disabled = isActive;
    if (plusBtn) plusBtn.disabled = isActive && newState !== 'stopped';
    if (voiceBtn) voiceBtn.disabled = isActive;

    // Show/hide overlays
    if (overlay) overlay.classList.toggle('active', newState === 'recording' || newState === 'paused');
    if (completeOverlay) completeOverlay.classList.toggle('active', newState === 'stopped');
    if (processingOverlay) processingOverlay.classList.toggle('active', newState === 'uploading' || newState === 'processing');

    // Any state change away from 'stopped' cancels a pending auto-upload countdown
    // (Process/Discard/Preview clicks all leave 'stopped' so this catches them all).
    if (newState !== 'stopped' && autouploadTimer) cancelAutoUploadCountdown();

    // Pulsing dot
    const dot = document.getElementById('rec-dot');
    if (dot) dot.classList.toggle('paused', newState === 'paused');

    // Pause/Resume button
    const pauseBtn = document.getElementById('rec-pause-btn');
    if (pauseBtn) {
        pauseBtn.innerHTML = newState === 'paused'
            ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>'
            : '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>';
        pauseBtn.title = newState === 'paused' ? 'Resume' : 'Pause';
    }
}

// ── Core Recording Functions ─────────────────────────────────────────────────

// Marker for the recording that's actively in progress. Set on start,
// cleared on clean stop/discard. Survives a crash — which is exactly the
// point: the recovery banner prompts ONLY for the marked recording. Old
// orphan fragments (no marker) surface in the Audio Manager as "Recovered
// Recording" entries instead of hijacking every page load. This is what
// fixes the "permanently stuck Resume/Discard prompt" bug: previously the
// banner fired for ANY recording whose last chunk wasn't status='complete',
// and several paths could leave chunks in that state forever.
const ACTIVE_RECORDING_KEY = 'ragsmith_active_recording';

function setActiveRecordingMarker(recId) {
    try { localStorage.setItem(ACTIVE_RECORDING_KEY, recId); } catch { /* private mode */ }
}

function clearActiveRecordingMarker() {
    try { localStorage.removeItem(ACTIVE_RECORDING_KEY); } catch { /* ignore */ }
}

function getActiveRecordingMarker() {
    try { return localStorage.getItem(ACTIVE_RECORDING_KEY); } catch { return null; }
}

export async function startRecording(opts = {}) {
    try {
        // resume=true means the caller (recovery banner) has already set
        // recordingId / chunkIndex / pausedDuration to continue an existing
        // fragment — do NOT reset them. This was the data-loss bug: resume
        // pre-assigned those three, then this function unconditionally
        // clobbered them with a fresh UUID + zeroes, so the "resumed"
        // recording was actually a brand-new one and the pre-crash fragment
        // stayed orphaned in IndexedDB (and was missing from the final
        // upload).
        if (!opts.resume) {
            recordingId = crypto.randomUUID();
            chunkIndex = 0;
            pausedDuration = 0;
        }
        pauseStart = 0;
        setActiveRecordingMarker(recordingId);

        const shareAudio = document.getElementById('rec-share-audio')?.checked;

        // Get microphone access. Use `ideal` (not exact) for sampleRate so
        // devices that don't natively support 16kHz still work (browser will
        // pick the closest rate). Exact constraints throw NotFoundError on
        // many laptops that only expose 44.1k/48k devices.
        //
        // "Capture room audio" toggles off the WebRTC voice-communication
        // pipeline (echo cancellation + noise suppression + auto gain). With
        // those on, the OS subtracts this device's speaker output from the
        // mic input — useful for VoIP but it erases anything playing on the
        // phone speaker. Off = mic picks up everything in the room, including
        // a podcast or speakerphone call playing from this device.
        const captureRoomAudio = document.getElementById('rec-room-audio')?.checked || false;
        audioStream = await navigator.mediaDevices.getUserMedia({
            audio: {
                // Echo-cancellation must stay OFF for room capture (it would
                // subtract the speaker output — i.e. erase what we're recording).
                echoCancellation: !captureRoomAudio,
                noiseSuppression: !captureRoomAudio,
                // Auto-gain stays ON even for room capture: a phone speaker right
                // next to its own mic easily overloads the input, which shows up
                // as static. AGC reins in the level WITHOUT cancelling the audio
                // the way echo-cancellation would.
                autoGainControl: true,
                sampleRate: { ideal: 16000 }
            }
        });

        // Optionally capture system audio
        if (shareAudio) {
            try {
                displayStream = await navigator.mediaDevices.getDisplayMedia({
                    video: true, audio: true, systemAudio: 'include'
                });
                // Video track is required by spec but we don't need it
                displayStream.getVideoTracks().forEach(t => t.stop());
            } catch (e) {
                // User cancelled the picker — fall back to mic-only
                console.log('System audio cancelled, recording mic only');
                displayStream = null;
            }
        }

        // Set up AudioContext and AnalyserNode
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const micSource = audioContext.createMediaStreamSource(audioStream);
        analyserNode = audioContext.createAnalyser();
        analyserNode.fftSize = 256;
        micSource.connect(analyserNode);

        // Determine the stream for MediaRecorder
        let recordStream = audioStream;

        if (displayStream && displayStream.getAudioTracks().length > 0) {
            // Mix mic + system audio via Web Audio API. The mic routes through a
            // GainNode so Mute can silence the mic path WITHOUT touching the
            // system audio feeding into the same mix.
            mixDestination = audioContext.createMediaStreamDestination();
            micGainNode = audioContext.createGain();
            micSource.connect(micGainNode);
            micGainNode.connect(mixDestination);
            const sysSource = audioContext.createMediaStreamSource(displayStream);
            sysSource.connect(mixDestination);
            recordStream = mixDestination.stream;

            // Handle user stopping screen share mid-recording
            displayStream.getAudioTracks()[0].onended = () => {
                console.log('System audio track ended — continuing with mic only');
                displayStream = null;
            };
        }

        // Create MediaRecorder
        const mimeType = MediaRecorder.isTypeSupported(MIME_TYPE) ? MIME_TYPE : 'audio/webm';
        mediaRecorder = new MediaRecorder(recordStream, { mimeType });

        mediaRecorder.ondataavailable = async (e) => {
            if (e.data.size > 0 && recordingId && recordingState !== 'stopped' && recordingState !== 'idle') {
                try {
                    // Track the in-flight write so stopRecording's onstop can
                    // await it. MediaRecorder fires the FINAL dataavailable
                    // right before stop; without this await the onstop
                    // handler's getChunks could run before the final chunk's
                    // transaction commits, mark the second-to-last chunk
                    // 'complete', and leave the true last chunk forever in
                    // 'recording' status.
                    lastChunkWrite = saveChunk(recordingId, chunkIndex++, e.data, getElapsed(), recordingState);
                    await lastChunkWrite;
                } catch (err) {
                    // DB may be closing during stop — safe to ignore
                    console.warn('[Audio] Chunk save failed (likely stopping):', err.message);
                }
            }
        };

        mediaRecorder.onstop = () => {
            // Final state handled by stopRecording()
        };

        // Start recording with timeslice for chunked output
        mediaRecorder.start(CHUNK_INTERVAL_MS);
        startTime = Date.now();

        // Reset silence detection state for the new recording
        silenceMs = 0;
        silenceTriggered = false;
        lastSilenceCheck = performance.now();

        // Honor the "start muted" preference. Toggling track.enabled is
        // applied after the stream is wired into MediaRecorder so the
        // recorded data is silent from the very first chunk.
        const startMuted = document.getElementById('rec-start-muted')?.checked === true;
        applyMute(startMuted);

        setRecordingState('recording');
        startTimer();
        startLevelMeter();
        await acquireWakeLock();
    } catch (e) {
        console.error('Failed to start recording:', e);
        cleanup();
        throw e;
    }
}

export function pauseRecording() {
    if (recordingState !== 'recording' || !mediaRecorder) return;

    mediaRecorder.pause();
    pausedDuration += (Date.now() - startTime) / 1000;
    pauseStart = Date.now();
    startTime = 0;

    setRecordingState('paused');
    stopLevelMeter();
    releaseWakeLock();
}

export async function resumeRecording() {
    if (recordingState !== 'paused' || !mediaRecorder) return;

    mediaRecorder.resume();
    startTime = Date.now();

    setRecordingState('recording');
    startLevelMeter();
    await acquireWakeLock();
}

export function togglePause() {
    if (recordingState === 'recording') pauseRecording();
    else if (recordingState === 'paused') resumeRecording();
}

export async function stopRecording() {
    if (!mediaRecorder || (recordingState !== 'recording' && recordingState !== 'paused')) return;

    // If paused, resume briefly to get final chunk
    if (recordingState === 'paused') {
        pausedDuration += 0; // already accumulated
    } else {
        pausedDuration += (Date.now() - startTime) / 1000;
    }

    return new Promise((resolve) => {
        mediaRecorder.onstop = async () => {
            // Wait for the final dataavailable write to commit before reading
            // chunks back — otherwise "last" might be the second-to-last
            // chunk and the true last one stays status='recording' forever.
            if (lastChunkWrite) {
                try { await lastChunkWrite; } catch { /* already logged */ }
            }
            // Mark chunks as complete
            const chunks = await getChunks(recordingId);
            if (chunks.length > 0) {
                const last = chunks[chunks.length - 1];
                await saveChunk(recordingId, last.chunkIndex, last.blob, pausedDuration, 'complete');
            }
            // Recording reached a clean stop — it's no longer "active", so a
            // crash after this point must NOT re-prompt resume for it.
            clearActiveRecordingMarker();

            stopTimer();
            stopLevelMeter();
            releaseWakeLock();

            // Stop tracks
            audioStream?.getTracks().forEach(t => t.stop());
            displayStream?.getTracks().forEach(t => t.stop());
            audioContext?.close();
            audioStream = null;
            displayStream = null;
            mixDestination = null;
            micGainNode = null;
            audioContext = null;
            analyserNode = null;
            mediaRecorder = null;

            // Update complete overlay info
            const durationEl = document.getElementById('rec-complete-duration');
            if (durationEl) durationEl.textContent = formatTime(pausedDuration);

            setRecordingState('stopped');
            resolve();
        };
        mediaRecorder.stop();
    });
}

// ── Preview Playback ─────────────────────────────────────────────────────────

let previewAudio = null;

export async function previewRecording() {
    if (!recordingId) return;

    const chunks = await getChunks(recordingId);
    if (!chunks.length) return;

    const blob = new Blob(chunks.map(c => c.blob), { type: 'audio/webm' });
    const url = URL.createObjectURL(blob);

    if (previewAudio) { previewAudio.pause(); URL.revokeObjectURL(previewAudio.src); }
    previewAudio = new Audio(url);
    previewAudio.play();

    // Update button to show playing state
    const btn = document.getElementById('rec-preview-btn');
    if (btn) btn.textContent = 'Playing...';
    previewAudio.onended = () => { if (btn) btn.textContent = 'Preview'; };
}

export function stopPreview() {
    if (previewAudio) {
        previewAudio.pause();
        URL.revokeObjectURL(previewAudio.src);
        previewAudio = null;
        const btn = document.getElementById('rec-preview-btn');
        if (btn) btn.textContent = 'Preview';
    }
}

// ── Discard ──────────────────────────────────────────────────────────────────

export async function discardRecording() {
    stopPreview();
    if (recordingId) await clearChunks(recordingId);
    clearActiveRecordingMarker();
    cleanup();
    setRecordingState('idle');
}

function cleanup() {
    stopTimer();
    stopLevelMeter();
    releaseWakeLock();
    if (mediaRecorder && mediaRecorder.state !== 'inactive') mediaRecorder.stop();
    audioStream?.getTracks().forEach(t => t.stop());
    displayStream?.getTracks().forEach(t => t.stop());
    audioContext?.close();
    mediaRecorder = null;
    audioStream = null;
    displayStream = null;
    mixDestination = null;
    micGainNode = null;
    audioContext = null;
    analyserNode = null;
    recordingId = null;
    chunkIndex = 0;
    startTime = 0;
    pausedDuration = 0;
}

// ── Upload Only ──────────────────────────────────────────────────────────────

async function uploadOnlyFlow(kbName, hfToken, existingFilePath) {
    hideError();
    const progressBar = document.getElementById('rec-progress-bar');
    const progressText = document.getElementById('rec-progress-text');
    if (progressBar) progressBar.style.width = '0%';

    if (existingFilePath) {
        // File already on server (reprocess from audio manager) — nothing to upload
        if (progressText) progressText.textContent = 'Already uploaded';
        if (progressBar) progressBar.style.width = '100%';
        setRecordingState('processing');
        setTimeout(() => {
            if (progressText) progressText.textContent = 'Uploaded — process later from Audio Manager';
            const errorActions = document.getElementById('rec-error-actions');
            if (errorActions) errorActions.style.display = 'flex';
        }, 500);
        return;
    }

    if (!recordingId) return;

    setRecordingState('uploading');
    stopPreview();

    try {
        const chunks = await getChunks(recordingId);
        if (!chunks.length) throw new Error('No recording data found');

        const filename = `recording_${new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-')}.webm`;

        if (progressText) progressText.textContent = 'Uploading...';
        const up = await uploadRecordingInParts(recordingId, chunks, filename, kbName, (text, pct) => {
            if (progressText) progressText.textContent = text;
            if (progressBar) progressBar.style.width = `${pct}%`;
        });

        // Clear IndexedDB chunks only after a COMPLETE upload. A salvaged one
        // is missing audio, and the phone copy is the only other record.
        if (!up.salvaged) await clearChunks(recordingId);

        if (progressBar) progressBar.style.width = '100%';
        if (progressText) progressText.textContent = up.salvaged
            ? salvageMessage(up)
            : 'Uploaded — process later from Audio Manager';
        setRecordingState('processing'); // Keep overlay visible for dismiss
        const errorActions = document.getElementById('rec-error-actions');
        if (errorActions) errorActions.style.display = 'flex';

    } catch (e) {
        console.error('Upload failed:', e);
        showError(e.message);
    }
}

// ── Save to Device Only ──────────────────────────────────────────────────────

async function saveToDeviceOnly(title) {
    if (!recordingId) return;

    const progressText = document.getElementById('rec-progress-text');
    const progressBar = document.getElementById('rec-progress-bar');

    setRecordingState('processing');
    stopPreview();

    try {
        const chunks = await getChunks(recordingId);
        if (!chunks.length) throw new Error('No recording data found');

        // Calculate duration from chunks
        const lastChunk = chunks[chunks.length - 1];
        const duration = lastChunk?.totalDuration || 0;

        // Save metadata to localStorage (chunks stay in IndexedDB)
        const savedRecordings = JSON.parse(localStorage.getItem('ragsmith_device_recordings') || '[]');
        savedRecordings.push({
            recordingId,
            title: title || `Recording ${new Date().toISOString().slice(0, 10)}`,
            date: new Date().toISOString(),
            duration,
            chunkCount: chunks.length,
            size: chunks.reduce((sum, c) => sum + (c.blob?.size || 0), 0),
        });
        localStorage.setItem('ragsmith_device_recordings', JSON.stringify(savedRecordings));

        if (progressBar) progressBar.style.width = '100%';
        if (progressText) progressText.textContent = 'Saved to device';
        setRecordingState('processing'); // Keep overlay for dismiss
        const errorActions = document.getElementById('rec-error-actions');
        if (errorActions) errorActions.style.display = 'flex';

    } catch (e) {
        console.error('Save to device failed:', e);
        showError(e.message);
    }
}

// A phone recording lives in the browser as many small blobs, and it used to
// be stitched into one before sending: a 61.8 MB blob built in a phone's
// memory and sent as a single request. It failed on every retry with a bare
// "Failed to fetch", and nginx, which records even an abandoned upload, never
// saw a byte of it. The same code had managed 54.8 MB a fortnight before,
// after one timed-out attempt, so this was a ceiling being reached rather
// than something that broke.
//
// So the recording goes up in pieces of a few megabytes. Only one piece is
// in memory at a time; a dropped connection costs that piece, not the lot;
// and the upload id comes from the recording itself, so after a refresh the
// recovery upload asks the server which pieces already arrived and sends
// only the rest.
// Small on purpose. At 4 MB a phone uploading to a server an ocean away
// filled its outgoing queue with one piece, HTTP/2's keep-alive ping waited
// in line behind the upload data, and Chrome declared the connection dead
// (ERR_HTTP2_PING_FAILED) about every twenty seconds without a single piece
// arriving. A megabyte finishes in seconds on the same link.
const UPLOAD_PART_BYTES = 1 * 1024 * 1024;
const UPLOAD_PART_TRIES = 4;

// The piece size is part of the id. Resuming lines pieces up by NUMBER, so
// pieces cut at one size and pieces cut at another must never meet in one
// upload: piece 3 of a 4 MB cut is not piece 3 of a 1 MB cut, and joining a
// mix would scramble the audio. A different size is simply a fresh upload.
function uploadIdFor(recId) {
    const size = Math.round(UPLOAD_PART_BYTES / 1024);
    const id = (`rec_${String(recId || 'unknown')}_p${size}`).replace(/[^A-Za-z0-9_-]/g, '_');
    return id.padEnd(8, '_').slice(0, 80);
}

// Each piece is read before it is sent. If the browser has lost the audio
// behind a stored chunk, no upload design can recover it, and the person
// needs to hear that plainly instead of retrying forever.
function unreadableChunkError(i, total, blob, e) {
    return new Error(
        `Part ${i + 1} of ${total} of this recording (${(blob.size / 1024).toFixed(0)} KB) can't be ` +
        `read back from this device's storage (${e.name}). The browser has lost the audio behind ` +
        `it, so the upload would fail however many times it is retried.`
    );
}

async function uploadRecordingInParts(recId, chunks, filename, kbName, onProgress) {
    const uploadId = uploadIdFor(recId);

    // SALVAGE. Read every stored chunk first and set aside any the browser
    // can no longer read. A one-hour recording failed every upload with
    // net::ERR_BLOB_REFERENCED_BLOB_BROKEN: the phone had kept the record
    // that a 30-second chunk existed but lost the audio behind it, and one
    // broken chunk made the whole hour unsendable. That chunk is gone for
    // good and no upload can bring it back. The rest is not, and refusing to
    // send it would lose it too.
    onProgress?.('Checking the recording...', 0);
    const readable = [];
    const broken = [];
    for (let i = 0; i < chunks.length; i++) {
        const b = chunks[i]?.blob;
        if (!(b instanceof Blob)) { broken.push(i); continue; }
        // One byte is enough to tell. A chunk whose stored audio is gone
        // fails on ANY read, and reading every chunk in full (the first cut)
        // pulled the whole hour into a phone's memory before sending a byte,
        // which is how the retry died with ERR_BLOB_OUT_OF_MEMORY.
        try {
            await b.slice(0, 1).arrayBuffer();
            if (b.size > 1) await b.slice(b.size - 1).arrayBuffer();
            readable.push({ i, b });
        } catch {
            broken.push(i);
        }
    }
    if (!readable.length) {
        throw new Error(
            `None of the ${chunks.length} parts of this recording can be read back from this ` +
            `device's storage. The browser has lost all of the audio, so there is nothing to upload.`
        );
    }
    if (broken.length) {
        console.warn(`[recorder] salvaging: ${broken.length} of ${chunks.length} parts unreadable`, broken);
    }

    const totalBytes = readable.reduce((sum, x) => sum + x.b.size, 0);
    const totalMb = (totalBytes / (1024 * 1024)).toFixed(1);

    // Group consecutive chunks into pieces. Deterministic for the same stored
    // chunks (and a broken chunk stays broken), which is what lets a refresh
    // line its pieces up with the ones the server already has.
    const groups = [];
    let cur = [];
    let curBytes = 0;
    for (const { i, b } of readable) {
        cur.push({ i, b });
        curBytes += b.size;
        if (curBytes >= UPLOAD_PART_BYTES) {
            groups.push(cur);
            cur = [];
            curBytes = 0;
        }
    }
    if (cur.length) groups.push(cur);

    let have = new Set();
    try {
        const r = await fetch(
            `${API_BASE}/import/audio/upload/parts?upload_id=${encodeURIComponent(uploadId)}`,
            { headers: getHeaders(false) }
        );
        if (r.ok) have = new Set((await r.json()).received || []);
    } catch { /* nothing known yet: send everything */ }

    let doneBytes = 0;
    for (let g = 0; g < groups.length; g++) {
        const pieceBytes = groups[g].reduce((sum, x) => sum + x.b.size, 0);
        // The last piece is always re-sent: it is the only one that can differ
        // between attempts, and it costs at most a few megabytes.
        if (have.has(g) && g < groups.length - 1) {
            doneBytes += pieceBytes;
            continue;
        }

        // A blob that REFERENCES the stored chunks, streamed from storage by
        // the browser as it sends. Copying each piece into memory and back
        // (the first cut) is what ran the phone out of blob memory; this is
        // the way the uploads that worked on this phone always sent.
        const piece = new Blob(groups[g].map(x => x.b), { type: 'application/octet-stream' });

        let lastErr = null;
        for (let attempt = 0; attempt < UPLOAD_PART_TRIES; attempt++) {
            try {
                const fd = new FormData();
                fd.append('upload_id', uploadId);
                fd.append('index', String(g));
                fd.append('file', piece, `part-${g}.bin`);
                const r = await fetch(`${API_BASE}/import/audio/upload/part`, {
                    method: 'POST',
                    headers: getHeaders(false),
                    body: fd,
                });
                if (!r.ok) {
                    const err = await r.json().catch(() => ({}));
                    throw new Error(err.detail || `HTTP ${r.status}`);
                }
                lastErr = null;
                break;
            } catch (e) {
                lastErr = e;
                await new Promise(res => setTimeout(res, 1500 * (attempt + 1)));
            }
        }
        if (lastErr) {
            throw new Error(
                `Piece ${g + 1} of ${groups.length} would not upload after ${UPLOAD_PART_TRIES} tries ` +
                `(${lastErr.message}). The pieces that arrived are kept on the server, and the ` +
                `recording is still on this device — retry and it carries on from piece ${g + 1}.`
            );
        }
        doneBytes += pieceBytes;
        const pct = totalBytes ? Math.round((doneBytes / totalBytes) * 100) : 100;
        onProgress?.(
            `Uploading ${(doneBytes / (1024 * 1024)).toFixed(1)} of ${totalMb} MB`
            + ` (piece ${g + 1} of ${groups.length})`,
            pct,
        );
    }

    const fd = new FormData();
    fd.append('upload_id', uploadId);
    fd.append('filename', filename);
    fd.append('count', String(groups.length));
    if (kbName) fd.append('title', kbName);
    const res = await fetch(`${API_BASE}/import/audio/upload/finish`, {
        method: 'POST',
        headers: getHeaders(false),
        body: fd,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        throw new Error(err.detail || 'Upload failed');
    }
    const out = await res.json();
    // The caller deletes the recording from this device once this returns.
    // So it only returns when the server holds exactly as many bytes as the
    // phone does: a success status is not the same thing as a complete copy,
    // and this is the one step after which there is no second copy.
    if (out.bytes !== totalBytes) {
        throw new Error(
            `The server joined ${out.bytes ?? 'an unknown number of'} bytes but this device has ` +
            `${totalBytes}. The recording is being KEPT on this device rather than deleted. ` +
            `Nothing has been lost; tell whoever maintains this that the sizes disagreed.`
        );
    }
    const lostSeconds = Math.round((broken.length * CHUNK_INTERVAL_MS) / 1000);
    return {
        ...out,
        // A salvaged upload is not the whole recording. The caller must keep
        // the phone copy, and say plainly what is missing.
        salvaged: broken.length > 0,
        lostParts: broken.map(i => i + 1),
        lostSeconds,
        // The first chunk carries the file header; without it the rest will
        // not play as a normal file, though the audio in it is still there.
        headerLost: broken.includes(0),
    };
}

function salvageMessage(up) {
    const m = Math.floor(up.lostSeconds / 60);
    const sec = up.lostSeconds % 60;
    const gap = m ? `${m} min ${sec} s` : `${sec} s`;
    const where = up.lostParts.length === 1 ? `part ${up.lostParts[0]}` : `parts ${up.lostParts.join(', ')}`;
    return `Uploaded everything this device could still read. About ${gap} is missing (${where}), ` +
        `because the phone had lost that audio before the upload began.` +
        (up.headerLost ? ` The missing part includes the file's header, so it may not play as a normal file yet.` : '') +
        ` The copy on this device has been KEPT.`;
}

// ── Upload + Process ─────────────────────────────────────────────────────────

export async function processRecording(kbName, hfToken, exportWav = false, exportMp3 = false, procOpts = {}) {
    if (!recordingId && !lastUploadedPath) return;

    lastKbName = kbName;
    lastHfToken = hfToken;
    lastProcArgs = { exportWav, exportMp3, procOpts };
    hideError();

    const progressBar = document.getElementById('rec-progress-bar');
    const progressText = document.getElementById('rec-progress-text');
    if (progressBar) progressBar.style.width = '0%';

    // If we already uploaded successfully before (retry case), skip upload
    if (lastUploadedPath) {
        setRecordingState('processing');
        if (progressText) progressText.textContent = 'Processing...';
        try {
            await processAudioSSE(lastUploadedPath, kbName, hfToken, exportWav, exportMp3, procOpts);
        } catch (e) {
            console.error('Processing failed:', e);
            showError(e.message);
        }
        return;
    }

    setRecordingState('uploading');
    stopPreview();

    try {
        // Assemble chunks into single blob
        const chunks = await getChunks(recordingId);
        if (!chunks.length) throw new Error('No recording data found');

        const filename = `recording_${new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-')}.webm`;

        // Upload
        if (progressText) progressText.textContent = 'Uploading...';
        const uploadData = await uploadRecordingInParts(recordingId, chunks, filename, kbName, (text, pct) => {
            if (progressText) progressText.textContent = text;
            if (progressBar) progressBar.style.width = `${pct}%`;
        });
        lastUploadedPath = uploadData.file_path; // Persist for retry

        // Clear IndexedDB chunks only after a COMPLETE upload. A salvaged one
        // is missing audio, and the phone copy is the only other record.
        if (uploadData.salvaged) {
            if (progressText) progressText.textContent = salvageMessage(uploadData);
        } else {
            await clearChunks(recordingId);
        }

        // Start SSE processing
        setRecordingState('processing');
        if (progressText) progressText.textContent = 'Processing...';

        await processAudioSSE(uploadData.file_path, kbName, hfToken, exportWav, exportMp3, procOpts);

    } catch (e) {
        console.error('Processing failed:', e);
        showError(e.message);
    }
}

export function retryProcessing() {
    if (!lastUploadedPath && !recordingId) return;
    const kbName = lastKbName || `recording-${new Date().toISOString().slice(0, 10)}`;
    const hfToken = lastHfToken || localStorage.getItem('ragsmith_hf_token') || '';
    const { exportWav = false, exportMp3 = false, procOpts = {} } = lastProcArgs || {};
    processRecording(kbName, hfToken, exportWav, exportMp3, procOpts);
}

export function stopProcessing() {
    if (processingAbortController) {
        const resolvePromise = processingAbortController._resolve;
        processingAbortController._userStopped = true;
        processingAbortController.abort();
        processingAbortController = null;

        // Immediately show stopped state — don't wait for fetch to clean up
        const progressText = document.getElementById('rec-progress-text');
        const progressBar = document.getElementById('rec-progress-bar');
        if (progressText) {
            progressText.textContent = 'Stopped — recording saved';
            progressText.classList.remove('error');
        }
        if (progressBar) progressBar.classList.remove('error');

        // Hide stop button (disabled state), show retry/dismiss
        const stopSection = document.getElementById('rec-stop-processing');
        if (stopSection) stopSection.style.display = 'none';
        const errorActions = document.getElementById('rec-error-actions');
        if (errorActions) errorActions.style.display = 'flex';

        // Show reprocess info
        const stopInfo = document.createElement('div');
        stopInfo.style.cssText = 'text-align:center; margin-top:10px; font-size:0.85rem; opacity:0.8; line-height:1.5;';
        stopInfo.innerHTML = '<b>Retry</b> to reprocess now, or <b>Dismiss</b> and reprocess later from the mic icon.';
        const processingBar = progressBar?.closest('.rec-bar');
        if (processingBar && !processingBar.querySelector('.stop-info')) {
            stopInfo.classList.add('stop-info');
            processingBar.appendChild(stopInfo);
        }

        // Resolve the promise so the caller doesn't hang
        if (resolvePromise) resolvePromise();
    }
}

export function dismissProcessing() {
    hideError();
    lastUploadedPath = null;
    lastKbName = null;
    lastHfToken = null;
    lastProcArgs = null;
    cleanup();
    setRecordingState('idle');
}

function showError(message) {
    setRecordingState('processing'); // Keep overlay visible
    const progressText = document.getElementById('rec-progress-text');
    const errorActions = document.getElementById('rec-error-actions');
    const progressBar = document.getElementById('rec-progress-bar');
    if (progressText) progressText.textContent = `Error: ${message}`;
    if (progressText) progressText.classList.add('error');
    if (progressBar) progressBar.classList.add('error');
    if (errorActions) errorActions.style.display = 'flex';
}

function hideError() {
    const progressText = document.getElementById('rec-progress-text');
    const errorActions = document.getElementById('rec-error-actions');
    const progressBar = document.getElementById('rec-progress-bar');
    if (progressText) progressText.classList.remove('error');
    if (progressBar) progressBar.classList.remove('error');
    if (errorActions) errorActions.style.display = 'none';
    // Remove any stop-info message
    const stopInfo = document.querySelector('.stop-info');
    if (stopInfo) stopInfo.remove();
}

function processAudioSSE(audioPath, kbName, hfToken, exportWav = false, exportMp3 = false, procOpts = {}) {
    return new Promise((resolve, reject) => {
        const progressBar = document.getElementById('rec-progress-bar');
        const progressText = document.getElementById('rec-progress-text');

        // Show stop button during processing
        const stopSection = document.getElementById('rec-stop-processing');
        if (stopSection) stopSection.style.display = '';

        // Create abort controller for stopping
        processingAbortController = new AbortController();
        // Store resolve so stopProcessing can trigger it directly
        processingAbortController._resolve = resolve;

        const {
            enableTranscript = true,
            enableSummary = true,
            enableSynopsis = false,
            enableDiarization = true,
            enableKb = true,
            denoise = false,
        } = procOpts;

        const transcribes = enableTranscript || enableSummary || enableDiarization;
        requestedStages = new Set(['converting']);
        if (transcribes) requestedStages.add('transcribing').add('building_transcript');
        if (enableDiarization) requestedStages.add('diarizing').add('aligning');
        if (enableDiarization && enableSummary) requestedStages.add('naming_speakers');
        if (enableSummary) requestedStages.add('summarizing');
        if (enableSynopsis) requestedStages.add('synopsis');
        if (enableKb) requestedStages.add('chunking').add('ingesting');

        // Use fetch + ReadableStream for SSE (supports POST with body)
        fetch(`${API_BASE}/import/audio`, {
            method: 'POST',
            headers: { ...getHeaders(false), 'Content-Type': 'application/json' },
            signal: processingAbortController.signal,
            body: JSON.stringify({
                audio_path: audioPath,
                kb_name: kbName,
                hf_token: hfToken || null,
                speaker_count: null,
                model: state.loadedModel || null,
                export_wav: exportWav,
                export_mp3: exportMp3,
                enable_transcript: enableTranscript,
                enable_summary: enableSummary,
                enable_synopsis: enableSynopsis,
                enable_diarization: enableDiarization,
                enable_kb: enableKb,
                denoise: denoise,
                // This page answers the question, so it may be asked.
                ask_speaker_names: true,
            }),
        }).then(async (res) => {
            if (!res.ok) {
                const err = await res.json().catch(() => ({ detail: 'Processing failed' }));
                throw new Error(err.detail || 'Processing failed');
            }

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                // Check if user stopped before waiting for next chunk
                if (processingAbortController?._userStopped) {
                    reader.cancel().catch(() => {});
                    throw new DOMException('Aborted', 'AbortError');
                }
                const { done, value } = await reader.read();
                if (done) break;
                // Check again after receiving chunk
                if (processingAbortController?._userStopped) {
                    reader.cancel().catch(() => {});
                    throw new DOMException('Aborted', 'AbortError');
                }

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop(); // Keep incomplete line

                let eventType = null;
                for (const line of lines) {
                    if (line.startsWith('event: ')) {
                        eventType = line.slice(7).trim();
                    } else if (line.startsWith('data: ') && eventType) {
                        try {
                            const data = JSON.parse(line.slice(6));
                            handleSSEEvent(eventType, data);
                        } catch (e) { /* ignore parse errors */ }
                        eventType = null;
                    } else if (line.startsWith(':')) {
                        // heartbeat, ignore
                    }
                }
            }

            if (stopSection) stopSection.style.display = 'none';
            processingAbortController = null;
            resolve();
        }).catch(err => {
            if (stopSection) stopSection.style.display = 'none';
            const wasStopped = processingAbortController?._userStopped;
            processingAbortController = null;

            if (err.name === 'AbortError' || wasStopped) {
                // User stopped processing — show friendly message with options
                if (progressText) {
                    progressText.textContent = 'Stopped — recording saved';
                    progressText.classList.remove('error');
                }
                if (progressBar) progressBar.classList.remove('error');
                // Reset stop button
                const stopBtn = document.getElementById('rec-stop-processing-btn');
                if (stopBtn) {
                    stopBtn.textContent = 'Stop Processing';
                    stopBtn.disabled = false;
                }
                const errorActions = document.getElementById('rec-error-actions');
                if (errorActions) errorActions.style.display = 'flex';
                // Show reprocess info with clear options
                const stopInfo = document.createElement('div');
                stopInfo.style.cssText = 'text-align:center; margin-top:10px; font-size:0.85rem; opacity:0.8; line-height:1.5;';
                stopInfo.innerHTML = '<b>Retry</b> to reprocess now, or <b>Dismiss</b> and reprocess later from the mic icon.';
                const processingBar = progressBar?.closest('.rec-bar');
                if (processingBar && !processingBar.querySelector('.stop-info')) {
                    stopInfo.classList.add('stop-info');
                    processingBar.appendChild(stopInfo);
                }
                resolve(); // Don't reject — the stop was intentional
            } else {
                reject(err);
            }
        });
    });
}

function handleSSEEvent(type, data) {
    const progressBar = document.getElementById('rec-progress-bar');
    const progressText = document.getElementById('rec-progress-text');

    switch (type) {
        case 'progress':
            if (data.stage === 'exporting_wav' || data.stage === 'exporting_mp3') {
                const isWav = data.stage === 'exporting_wav';
                const starting = data.stage_current === 0;
                const done = data.stage_current >= data.stage_total;
                // Skip if completed immediately (format wasn't selected)
                if (done && !window._exportStarted?.[data.stage]) break;
                if (starting) {
                    window._exportStarted = window._exportStarted || {};
                    window._exportStarted[data.stage] = true;
                    const exportSection = document.getElementById('rec-export-progress');
                    if (exportSection) exportSection.style.display = '';
                }
                const bar = document.getElementById(isWav ? 'rec-export-wav-bar' : 'rec-export-mp3-bar');
                const text = document.getElementById(isWav ? 'rec-export-wav-text' : 'rec-export-mp3-text');
                if (bar) bar.style.width = done ? '100%' : '10%';
                if (text) text.textContent = done
                    ? (isWav ? 'WAV ready' : 'MP3 ready')
                    : (isWav ? 'Converting WAV...' : 'Converting MP3...');
            } else {
                if (progressBar) progressBar.style.width = `${data.percent}%`;
                // A stage the chips left off still reports itself as done, so
                // the bar moves past it without its label ever showing.
                if (requestedStages && !requestedStages.has(data.stage)) break;
                if (progressText) {
                    const stageLabels = {
                        converting: 'Converting audio...',
                        transcribing: 'Transcribing audio...',
                        diarizing: 'Identifying speakers...',
                        aligning: 'Aligning speakers with transcript...',
                        naming_speakers: 'Waiting for speaker names...',
                        building_transcript: 'Building transcript...',
                        // Two distinct multi-minute passes, named for what
                        // each one actually produces. "summarizing" builds the
                        // long narrative; the synopsis is written from it
                        // afterwards. One shared label left the bar claiming
                        // to generate a summary through both.
                        summarizing: 'Writing narrative...',
                        synopsis: 'Generating summary...',
                        chunking: 'Preparing for knowledge base...',
                        ingesting: 'Adding to knowledge base...',
                    };
                    progressText.textContent = stageLabels[data.stage] || data.stage;
                }
            }
            break;

        case 'need_names':
            showNamesFirstDialog(data);
            break;

        case 'speakers':
            // Store for the KB picker dialog
            window._audioImportSpeakers = data.speakers;
            break;

        case 'transcript':
            // Store preview for display
            window._audioImportTranscript = data.preview;
            break;

        case 'complete': {
            if (progressBar) progressBar.style.width = '100%';
            if (progressText) progressText.textContent = 'Complete!';
            // Hide export bars and reset tracker
            const exportDone = document.getElementById('rec-export-progress');
            if (exportDone) exportDone.style.display = 'none';
            window._exportStarted = {};
            window._audioImportResult = data;
            showKBPickerDialog(data);
            break;
        }

        case 'error':
            showError(data.message);
            break;

        case 'done':
            break;
    }
}

// ── KB Picker Dialog ─────────────────────────────────────────────────────────

// Context for the currently-open speaker rename/edit dialog, consumed by
// the transcript/summary reference buttons inside it.
let _speakerDialogContext = null;

function speakerRows(speakers) {
    return Object.entries(speakers).map(([label, name]) =>
        `<div class="rec-speaker-row">
            <span class="rec-speaker-label">${label}</span>
            <input type="text" class="rec-speaker-input" data-speaker="${label}" value="${name}" placeholder="Enter name...">
        </div>`
    ).join('');
}

// The job has found its speakers and is waiting: the narrative is written with
// whatever names come back from here.
function showNamesFirstDialog({ job_id, speakers, has_transcript, title, source_filename }) {
    const dialog = document.getElementById('recording-kb-dialog');
    if (!dialog) return;
    const count = Object.keys(speakers).length;

    const titleEl = dialog.querySelector('.rec-dialog-title');
    if (titleEl) titleEl.textContent = 'Who is speaking?';
    const statsEl = dialog.querySelector('.rec-kb-stats');
    if (statsEl) {
        statsEl.textContent = `${count} speaker${count !== 1 ? 's' : ''} found. The narrative and summary will use these names.`;
    }
    const speakersSection = dialog.querySelector('#rec-speakers-section');
    if (speakersSection) speakersSection.style.display = '';
    const speakersContainer = dialog.querySelector('.rec-speakers-list');
    if (speakersContainer) speakersContainer.innerHTML = speakerRows(speakers);

    const cancelBtn = dialog.querySelector('#rec-kb-cancel');
    const confirmBtn = dialog.querySelector('#rec-kb-confirm');
    if (cancelBtn) { cancelBtn.textContent = 'Skip'; cancelBtn.style.display = ''; }
    if (confirmBtn) { confirmBtn.textContent = 'Save Names'; confirmBtn.style.display = ''; }

    _speakerDialogContext = {
        mode: 'names-first',
        jobId: job_id,
        filename: source_filename || '',
        title: title || '',
        hasTranscript: Boolean(has_transcript),
        hasSummary: false,
        showRefs: true,
    };
    updateSpeakerDialogRefButtons();
    dialog.classList.add('active');
}

// Answer the waiting job. Hides the dialog either way: the job moves on.
async function answerNamesFirst(speakerMap) {
    const dialog = document.getElementById('recording-kb-dialog');
    const jobId = _speakerDialogContext?.jobId;
    const named = Object.keys(speakerMap).length > 0;
    if (dialog) dialog.classList.remove('active');
    _speakerDialogContext = null;
    try {
        const res = await fetch(`${API_BASE}/import/audio/jobs/${jobId}/speakers`, {
            method: 'POST',
            headers: { ...getHeaders(false), 'Content-Type': 'application/json' },
            body: JSON.stringify({ speaker_map: speakerMap }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        if (named) window._speakersNamedFirst = true;
        // No stream to show the rest on: this page came back to a job that
        // was already waiting.
        if (!processingAbortController) showToast('Finishing the recording in the background.');
    } catch (e) {
        console.error('Could not send speaker names:', e);
        showToast('Could not send the names. Set them from the recording once it finishes.');
    }
}

// A job waits for its names whether or not this page is still watching it. A
// closed tab or a dropped stream comes back here and finds the question.
async function checkForWaitingNames() {
    const dialog = document.getElementById('recording-kb-dialog');
    if (!dialog || dialog.classList.contains('active')) return;
    try {
        const res = await fetch(`${API_BASE}/import/audio/jobs`, { headers: getHeaders(false) });
        if (!res.ok) return;
        const { jobs } = await res.json();
        const job = (jobs || []).find(j => j.naming);
        if (!job || dialog.classList.contains('active')) return;
        showNamesFirstDialog({
            job_id: job.job_id,
            speakers: job.naming.speakers,
            has_transcript: job.naming.has_transcript,
            title: job.naming.title,
            source_filename: job.filename,
        });
    } catch (e) { /* offline: the next look will find it */ }
}

async function showKBPickerDialog(result) {
    const dialog = document.getElementById('recording-kb-dialog');
    if (!dialog) return;

    const speakers = window._audioImportSpeakers || {};
    const speakerKeys = Object.keys(speakers);
    const kbIngested = (result?.total_chunks || 0) > 0;
    // Always show rename inputs when any speakers were detected — even a single
    // SPEAKER_00 is useful to name. Dialog still works without a KB (renames
    // are persisted into transcript/summary/meta on the recording itself).
    // Named before the narrative was written: nothing left to ask.
    const showSpeakerRename = speakerKeys.length > 0 && !window._speakersNamedFirst;
    delete window._speakersNamedFirst;

    const dialogTitle = dialog.querySelector('.rec-dialog-title');
    if (dialogTitle) dialogTitle.textContent = 'Processing Complete';

    // Populate stats — adapt wording to whatever actually ran
    const statsEl = dialog.querySelector('.rec-kb-stats');
    if (statsEl) {
        const parts = [];
        if (kbIngested) parts.push(`${result.total_chunks} chunk${result.total_chunks !== 1 ? 's' : ''}`);
        if (result.speakers) parts.push(`${result.speakers} speaker${result.speakers !== 1 ? 's' : ''}`);
        if (result.duration) parts.push(result.duration);
        statsEl.textContent = parts.join(', ') || 'Done.';
    }

    // Speakers section (rename inputs) — only show when meaningful
    const speakersSection = dialog.querySelector('#rec-speakers-section');
    const speakersContainer = dialog.querySelector('.rec-speakers-list');
    if (speakersSection) speakersSection.style.display = showSpeakerRename ? '' : 'none';
    // Cleared when hidden: Dismiss reads these inputs, and the ones left over
    // from the names-first step would be saved a second time.
    if (speakersContainer) speakersContainer.innerHTML = showSpeakerRename ? speakerRows(speakers) : '';

    // Adapt the actions: speaker rename flow has Skip/Save, otherwise a single Dismiss
    const cancelBtn = dialog.querySelector('#rec-kb-cancel');
    const confirmBtn = dialog.querySelector('#rec-kb-confirm');
    if (showSpeakerRename) {
        if (cancelBtn) { cancelBtn.textContent = 'Skip'; cancelBtn.style.display = ''; }
        if (confirmBtn) { confirmBtn.textContent = 'Save Names'; confirmBtn.style.display = ''; }
    } else {
        if (cancelBtn) cancelBtn.style.display = 'none';
        if (confirmBtn) { confirmBtn.textContent = 'Dismiss'; confirmBtn.style.display = ''; }
    }

    // Reference buttons: only surface them when we're actually asking the user
    // to assign speaker names. The plain "Done/Dismiss" completion state doesn't
    // need transcript/summary buttons — they're accessible from the file list.
    _speakerDialogContext = {
        mode: 'post-process',
        filename: result?.source_filename || '',
        title: result?.kb_name || '',
        hasTranscript: Boolean(result?.transcript_path),
        hasSummary: Boolean(result?.summary_path),
        showRefs: showSpeakerRename,
    };
    updateSpeakerDialogRefButtons();

    setRecordingState('idle');
    dialog.classList.add('active');
}

function updateSpeakerDialogRefButtons() {
    const refsEl = document.getElementById('rec-kb-refs');
    const tBtn = document.getElementById('rec-kb-ref-transcript');
    const sBtn = document.getElementById('rec-kb-ref-summary');
    const ctx = _speakerDialogContext;
    // Only show refs when the dialog is in the speaker-naming step AND we have
    // a filename + at least one artifact to open.
    const showRow = !!(ctx && ctx.showRefs && ctx.filename && (ctx.hasTranscript || ctx.hasSummary));
    if (refsEl) refsEl.style.display = showRow ? '' : 'none';
    if (tBtn) tBtn.disabled = !(ctx && ctx.hasTranscript);
    if (sBtn) sBtn.disabled = !(ctx && ctx.hasSummary);
}

// Opens the speaker-rename dialog pre-populated from an existing file record,
// for editing speaker names after initial processing has finished.
async function openSpeakerEditDialog(fileRecord) {
    const dialog = document.getElementById('recording-kb-dialog');
    if (!dialog) return;

    // Speakers map: prefer top-level (works without KB), fall back to latest KB import
    const latest = fileRecord.kb_imports?.[fileRecord.kb_imports.length - 1] || null;
    let speakers = fileRecord.speakers && Object.keys(fileRecord.speakers).length
        ? { ...fileRecord.speakers }
        : (latest?.speakers ? { ...latest.speakers } : {});
    // Synthesize from speaker_count if nothing else is available
    if (!Object.keys(speakers).length && fileRecord.speaker_count) {
        for (let i = 0; i < fileRecord.speaker_count; i++) {
            speakers[`SPEAKER_${String(i).padStart(2, '0')}`] = `Speaker ${i + 1}`;
        }
    }
    const speakerKeys = Object.keys(speakers);
    if (!speakerKeys.length) return;

    // Stash payload for the save handler
    window._audioImportSpeakers = { ...speakers };
    window._audioImportResult = {
        kb_name: latest?.kb_name || '',
        source_filename: fileRecord.filename,
        total_chunks: latest?.total_chunks || 0,
        duration: '',
        speakers: speakerKeys.length,
    };

    // Title + stats
    const titleEl = dialog.querySelector('.rec-dialog-title');
    if (titleEl) titleEl.textContent = 'Edit Speaker Names';
    const statsEl = dialog.querySelector('.rec-kb-stats');
    if (statsEl) {
        const parts = [];
        if (latest?.kb_name) parts.push(`KB: ${latest.kb_name}`);
        parts.push(`${speakerKeys.length} speaker${speakerKeys.length !== 1 ? 's' : ''}`);
        if (latest?.total_chunks) parts.push(`${latest.total_chunks} chunks`);
        statsEl.textContent = parts.join(' · ') || fileRecord.title;
    }

    // Populate input rows
    const speakersSection = dialog.querySelector('#rec-speakers-section');
    const speakersContainer = dialog.querySelector('.rec-speakers-list');
    if (speakersSection) speakersSection.style.display = '';
    if (speakersContainer) {
        speakersContainer.innerHTML = Object.entries(speakers).map(([label, name]) =>
            `<div class="rec-speaker-row">
                <span class="rec-speaker-label">${label}</span>
                <input type="text" class="rec-speaker-input" data-speaker="${label}" value="${name && name !== label ? name : ''}" placeholder="Enter name...">
            </div>`
        ).join('');
    }

    // Actions: Cancel + Save Names
    const cancelBtn = dialog.querySelector('#rec-kb-cancel');
    const confirmBtn = dialog.querySelector('#rec-kb-confirm');
    if (cancelBtn) { cancelBtn.textContent = 'Cancel'; cancelBtn.style.display = ''; }
    if (confirmBtn) { confirmBtn.textContent = 'Save Names'; confirmBtn.style.display = ''; }

    _speakerDialogContext = {
        mode: 'edit',
        filename: fileRecord.filename,
        title: fileRecord.title || fileRecord.filename,
        hasTranscript: !!fileRecord.has_transcript,
        hasSummary: !!fileRecord.has_summary,
        showRefs: true,  // edit flow always surfaces the refs
    };
    updateSpeakerDialogRefButtons();

    dialog.classList.add('active');
}

export async function confirmKBImport() {
    const dialog = document.getElementById('recording-kb-dialog');
    if (!dialog) return;

    // Check for speaker renames
    const speakers = {};
    dialog.querySelectorAll('.rec-speaker-input').forEach(input => {
        const label = input.dataset.speaker;
        const name = input.value.trim();
        if (name && name !== label) speakers[label] = name;
    });

    if (_speakerDialogContext?.mode === 'names-first') {
        await answerNamesFirst(speakers);
        return;
    }

    const result = window._audioImportResult;
    if (!result) return;

    // Rename speakers if any changed. Include both kb_name (for KB chunk rewrite)
    // and filename (for on-disk transcript/summary/cache/meta rewrite) so renames
    // persist even if the user skipped KB ingestion.
    if (Object.keys(speakers).length > 0) {
        try {
            await fetch(`${API_BASE}/import/audio/rename-speakers`, {
                method: 'POST',
                headers: { ...getHeaders(false), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    kb_name: result.kb_name || null,
                    filename: result.source_filename || null,
                    speaker_map: speakers,
                }),
            });
        } catch (e) {
            console.error('Speaker rename failed:', e);
        }
    }

    dialog.classList.remove('active');
    lastUploadedPath = null;
    lastKbName = null;
    lastHfToken = null;
    lastProcArgs = null;
    cleanup();

    // Clean up temp state
    delete window._audioImportSpeakers;
    delete window._audioImportTranscript;
    delete window._audioImportResult;
}

export function cancelKBImport() {
    if (_speakerDialogContext?.mode === 'names-first') {
        answerNamesFirst({});
        return;
    }
    const dialog = document.getElementById('recording-kb-dialog');
    if (dialog) dialog.classList.remove('active');
    lastUploadedPath = null;
    lastKbName = null;
    lastHfToken = null;
    lastProcArgs = null;
    cleanup();
    delete window._audioImportSpeakers;
    delete window._audioImportTranscript;
    delete window._audioImportResult;
}

// ── Recovery ─────────────────────────────────────────────────────────────────

export async function checkForIncompleteRecording() {
    try {
        // Only prompt for the recording explicitly marked active — i.e. one
        // that crashed mid-recording without reaching a clean stop. Any other
        // orphaned chunks in IndexedDB (from older sessions, pre-fix bugs,
        // dismissed banners) surface in the Audio Manager as "Recovered
        // Recording" entries instead, where they can be uploaded, merged, or
        // deliberately deleted. This makes the banner impossible to get
        // permanently stuck: it only ever refers to the marker, and every
        // banner action clears the marker.
        const activeId = getActiveRecordingMarker();
        if (!activeId) return;

        const chunks = await getChunks(activeId);
        if (!chunks.length) {
            // Marker points at nothing (chunks already cleared) — stale.
            clearActiveRecordingMarker();
            return;
        }

        const latest = chunks[chunks.length - 1];
        if (latest.status === 'complete') {
            // Recording finished cleanly but the marker survived (e.g. crash
            // between mark-complete and marker-clear). Nothing to resume.
            clearActiveRecordingMarker();
            return;
        }

        const banner = document.getElementById('recording-recovery-banner');
        if (!banner) return;

        const durationEl = banner.querySelector('.rec-recovery-duration');
        if (durationEl) durationEl.textContent = formatTime(latest.totalDuration || 0);

        banner.classList.add('active');

        // Wire up buttons
        const resumeBtn = banner.querySelector('.rec-recovery-resume');
        const discardBtn = banner.querySelector('.rec-recovery-discard');

        resumeBtn?.addEventListener('click', async () => {
            banner.classList.remove('active');
            recordingId = activeId;
            chunkIndex = chunks.length;
            pausedDuration = latest.totalDuration || 0;
            startTime = 0;
            // resume:true — without it startRecording would clobber the
            // three values above with a fresh UUID and zeroes, recording a
            // NEW session while the crashed fragment stayed orphaned.
            await startRecording({ resume: true });
        }, { once: true });

        // "Save for later" — non-destructive. Clears the marker so the
        // banner stops appearing; the fragment stays in IndexedDB and shows
        // up in the Audio Manager as a Recovered Recording (uploadable,
        // mergeable, or deletable there with a confirm dialog). The old
        // behavior deleted the chunks outright, which silently destroyed
        // the only copy of the pre-crash portion of a meeting.
        discardBtn?.addEventListener('click', () => {
            banner.classList.remove('active');
            clearActiveRecordingMarker();
        }, { once: true });
    } catch (e) {
        console.warn('Recovery check failed:', e);
    }
}

// ── Audio Manager Modal ──────────────────────────────────────────────────────

export function openAudioManager() {
    const modal = document.getElementById('audio-manager-modal');
    if (!modal) return;
    modal.classList.remove('hidden');
    modal.classList.add('active');
    refreshAudioFileList();
}

export function closeAudioManager() {
    const modal = document.getElementById('audio-manager-modal');
    if (!modal) return;
    modal.classList.remove('active');
    modal.classList.add('hidden');
}

function formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDurationShort(seconds) {
    if (!seconds) return 'Unknown';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}

async function refreshAudioFileList() {
    const listEl = document.getElementById('audio-file-list');
    if (!listEl) return;

    listEl.innerHTML = '<div class="audio-file-empty">Loading...</div>';

    try {
        const res = await fetch(`${API_BASE}/import/audio/files`, { headers: getHeaders(false) });
        if (!res.ok) throw new Error('Failed to load files');
        const files = await res.json();

        // Include device-only recordings from localStorage/IndexedDB
        const deviceRecordings = JSON.parse(localStorage.getItem('ragsmith_device_recordings') || '[]');
        const knownIds = new Set(deviceRecordings.map(dr => dr.recordingId));
        const deviceFiles = [];
        for (const dr of deviceRecordings) {
            // Verify chunks still exist in IndexedDB
            try {
                const chunks = await getChunks(dr.recordingId);
                if (chunks.length > 0) {
                    deviceFiles.push({
                        ...dr,
                        _device_only: true,
                        title: dr.title || 'Untitled',
                        size: dr.size || chunks.reduce((sum, c) => sum + (c.blob?.size || 0), 0),
                        format: 'webm',
                        created: new Date(dr.date).getTime() / 1000,
                        duration: dr.duration || null,
                    });
                }
            } catch { /* chunks gone */ }
        }

        // Scan IndexedDB for orphaned recordings not in localStorage registry
        // (e.g. from crashed stop flows or interrupted sessions)
        try {
            const idb = await openDB();
            const tx = idb.transaction(STORE_NAME, 'readonly');
            const store = tx.objectStore(STORE_NAME);
            const allKeys = await new Promise((res, rej) => {
                const req = store.getAllKeys();
                req.onsuccess = () => res(req.result);
                req.onerror = () => rej(req.error);
            });
            // Extract unique recording IDs from [recordingId, chunkIndex] keys
            const orphanIds = new Set();
            for (const key of allKeys) {
                const rid = Array.isArray(key) ? key[0] : key;
                if (rid && !knownIds.has(rid)) orphanIds.add(rid);
            }
            for (const rid of orphanIds) {
                try {
                    const chunks = await getChunks(rid);
                    if (chunks.length > 0) {
                        const lastChunk = chunks[chunks.length - 1];
                        const totalSize = chunks.reduce((sum, c) => sum + (c.blob?.size || 0), 0);
                        deviceFiles.push({
                            recordingId: rid,
                            _device_only: true,
                            _orphaned: true,
                            title: 'Recovered Recording',
                            size: totalSize,
                            format: 'webm',
                            created: (lastChunk.timestamp || Date.now()) / 1000,
                            duration: lastChunk.totalDuration || null,
                        });
                    }
                } catch { /* skip */ }
            }
        } catch { /* IDB scan failed, not critical */ }

        const allFiles = [...deviceFiles, ...files];

        if (!allFiles.length) {
            listEl.innerHTML = '<div class="audio-file-empty">No recordings yet</div>';
            return;
        }

        // Use allFiles instead of files for rendering
        const filesToRender = allFiles;

        listEl.innerHTML = filesToRender.map(f => {
            const isDevice = f._device_only || false;
            const date = new Date(f.created * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
            const hasImports = !isDevice && f.kb_imports && f.kb_imports.length > 0;
            const latestImport = hasImports ? f.kb_imports[f.kb_imports.length - 1] : null;
            const kbBadge = isDevice
                ? '<span class="audio-file-kb-badge" style="background:#f59e0b;color:#000">Device Only</span>'
                : latestImport
                    ? `<span class="audio-file-kb-badge" title="Imported ${latestImport.imported_at ? new Date(latestImport.imported_at).toLocaleDateString() : ''}">KB: ${latestImport.kb_name} (${latestImport.total_chunks} chunks)</span>`
                    : '';

            const title = f.title || f.filename;
            const titleSlug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
            const dateSlug = new Date(f.created * 1000).toISOString().slice(0, 10);
            const downloadName = `${titleSlug}-${dateSlug}`;
            const speakerInfo = f.speaker_count ? `${f.speaker_count} speaker${f.speaker_count !== 1 ? 's' : ''}` : '';

            const savedKbName = latestImport?.kb_name || f.title || '';
            const displayTitle = (f.title || f.filename || '').replace(/"/g, '&quot;');
            return `<div class="audio-file-item" data-filepath="${f.file_path || ''}" data-filename="${f.filename || ''}" data-title="${displayTitle}" data-download-name="${downloadName}" data-saved-kbname="${savedKbName.replace(/"/g, '&quot;')}" data-has-transcript="${f.has_transcript ? '1' : '0'}" data-has-summary="${f.has_summary ? '1' : '0'}" data-has-kb="${hasImports ? '1' : '0'}" ${isDevice ? `data-device-recording-id="${f.recordingId}" data-created="${f.created || 0}"` : ''}>
                <div class="audio-file-header">
                    ${isDevice ? `<label class="audio-merge-check" title="Select for merge">
                        <input type="checkbox" class="merge-select" data-recording-id="${f.recordingId}" data-created="${f.created || 0}">
                    </label>` : ''}
                    <div class="audio-file-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                            <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                            <line x1="12" y1="19" x2="12" y2="23"/>
                            <line x1="8" y1="23" x2="16" y2="23"/>
                        </svg>
                    </div>
                    <div class="audio-file-info">
                        <div class="audio-file-name">${title}</div>
                        <div class="audio-file-meta">
                            <span>${formatDurationShort(f.duration)}</span>
                            <span>${formatFileSize(f.size)}</span>
                            <span>${f.format.toUpperCase()}</span>
                            ${speakerInfo ? `<span>${speakerInfo}</span>` : ''}
                        </div>
                    </div>
                    <button class="audio-file-btn delete-btn" title="Delete recording">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14H7L5 6"/>
                            <path d="M10 11v6"/><path d="M14 11v6"/>
                        </svg>
                    </button>
                </div>
                <div class="audio-file-date">${date}</div>
                ${kbBadge ? `<div class="audio-file-kbs">${kbBadge}</div>` : ''}
                <div class="audio-file-actions">
                    ${f.has_transcript ? `<button class="audio-file-btn transcript-btn" title="View transcript">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/>
                            <line x1="16" y1="17" x2="8" y2="17"/>
                        </svg>
                        Transcript
                    </button>` : ''}
                    ${f.has_summary ? `<button class="audio-file-btn summary-btn" title="View the full narrative account of this recording">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="21" y1="10" x2="3" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/>
                            <line x1="15" y1="14" x2="3" y2="14"/><line x1="11" y1="18" x2="3" y2="18"/>
                        </svg>
                        Narrative
                    </button>` : ''}
                    ${f.has_summary ? `<button class="audio-file-btn synopsis-btn" title="${f.has_synopsis ? 'View the brief summary' : 'Write a brief summary from the narrative'}">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                            <line x1="15" y1="13" x2="9" y2="13"/><line x1="13" y1="17" x2="9" y2="17"/>
                        </svg>
                        ${f.has_synopsis ? 'Summary' : 'Summarize'}
                    </button>` : ''}
                    ${(f.has_transcript || f.has_summary) && !isDevice ? `<button class="audio-file-btn ingest-conv-btn" title="Put this recording into the open conversation's memory">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        Ingest
                    </button>` : ''}
                    ${!isDevice && (f.speaker_count || 0) > 0 ? `<button class="audio-file-btn edit-speakers-btn" title="Edit speaker names">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                            <circle cx="12" cy="7" r="4"/>
                        </svg>
                        Speakers
                    </button>` : ''}
                    <button class="audio-file-btn ${isDevice ? 'upload-device-btn' : 'process-btn'}" title="${isDevice ? 'Upload to server' : hasImports ? 'Reprocess' : 'Process'} into KB">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            ${isDevice
                                ? '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>'
                                : '<polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>'}
                        </svg>
                        ${isDevice ? 'Upload' : hasImports ? 'Reprocess' : 'Process'}
                    </button>
                    <span class="audio-file-actions-spacer"></span>
                    ${isDevice ? '' : `<button class="audio-file-btn download-btn${f.has_wav ? '' : ' unavailable'}" data-format="wav" title="${f.has_wav ? 'Download as WAV' : 'WAV not converted yet'}" ${f.has_wav ? '' : 'disabled'}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        WAV
                    </button>
                    <button class="audio-file-btn download-btn${f.has_mp3 ? '' : ' unavailable'}" data-format="mp3" title="${f.has_mp3 ? 'Download as MP3' : 'MP3 not converted yet'}" ${f.has_mp3 ? '' : 'disabled'}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        MP3
                    </button>`}
                </div>
            </div>`;
        }).join('');

        // Wire process buttons (server recordings)
        listEl.querySelectorAll('.process-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const item = e.target.closest('.audio-file-item');
                const filePath = item?.dataset.filepath;
                if (!filePath) return;
                const savedKbName = item?.dataset.savedKbname || '';
                const existing = {
                    hasTranscript: item?.dataset.hasTranscript === '1',
                    hasSummary: item?.dataset.hasSummary === '1',
                    hasKb: item?.dataset.hasKb === '1',
                };
                closeAudioManager();
                processExistingFile(filePath, savedKbName, existing);
            });
        });

        // Wire upload buttons (device-only recordings)
        listEl.querySelectorAll('.upload-device-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const item = e.target.closest('.audio-file-item');
                const recId = item?.dataset.deviceRecordingId;
                if (!recId) return;
                // Get saved title from localStorage
                const saved = JSON.parse(localStorage.getItem('ragsmith_device_recordings') || '[]');
                const savedTitle = saved.find(r => r.recordingId === recId)?.title || null;
                closeAudioManager();
                // Set recordingId so the pre-process dialog can upload it.
                // Clearing lastUploadedPath is load-bearing: processRecording()
                // short-circuits past the upload whenever it is set, so a value
                // left over from anything touched earlier in this page session
                // made a device recording silently never upload — it tried to
                // process the OLD server path instead. Reloading the page hid
                // the bug, which is why it survived so long.
                recordingId = recId;
                lastUploadedPath = null;
                lastKbName = null;
                lastHfToken = null;
                lastProcArgs = null;
                showPreProcessDialog(null, savedTitle);
            });
        });

        // Wire delete buttons
        listEl.querySelectorAll('.delete-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const item = e.target.closest('.audio-file-item');
                const deviceRecId = item?.dataset.deviceRecordingId;

                if (deviceRecId) {
                    // Device-only recording — delete from IndexedDB + localStorage
                    const confirmed = await confirmDialog({
                        title: 'Delete Recording',
                        message: 'Delete this device recording? This cannot be undone.',
                        confirmText: 'Delete',
                        danger: true,
                    });
                    if (!confirmed) return;
                    try {
                        await clearChunks(deviceRecId);
                        const saved = JSON.parse(localStorage.getItem('ragsmith_device_recordings') || '[]');
                        localStorage.setItem('ragsmith_device_recordings',
                            JSON.stringify(saved.filter(r => r.recordingId !== deviceRecId)));
                        refreshAudioFileList();
                    } catch (err) {
                        console.error('Delete failed:', err);
                    }
                    return;
                }

                const filename = item?.dataset.filename;
                if (!filename) return;
                const confirmed = await confirmDialog({
                    title: 'Delete Recording',
                    message: `Delete ${filename}? This cannot be undone.`,
                    confirmText: 'Delete',
                    danger: true,
                });
                if (!confirmed) return;
                try {
                    const delRes = await fetch(`${API_BASE}/import/audio/files/${encodeURIComponent(filename)}`, {
                        method: 'DELETE',
                        headers: getHeaders(false),
                    });
                    if (!delRes.ok) throw new Error('Delete failed');
                    refreshAudioFileList();
                } catch (err) {
                    console.error('Delete failed:', err);
                }
            });
        });
        // Wire transcript buttons
        listEl.querySelectorAll('.transcript-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const item = e.target.closest('.audio-file-item');
                const filename = item?.dataset.filename;
                if (!filename) return;
                await showTranscriptViewer(filename, item?.dataset.title || '');
            });
        });
        // Wire summary buttons
        listEl.querySelectorAll('.summary-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const item = e.target.closest('.audio-file-item');
                const filename = item?.dataset.filename;
                if (!filename) return;
                await showSummaryViewer(filename, item?.dataset.title || '');
            });
        });
        // Wire summary (synopsis) buttons
        listEl.querySelectorAll('.synopsis-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const item = e.target.closest('.audio-file-item');
                const filename = item?.dataset.filename;
                if (!filename) return;
                await showSynopsisViewer(filename, item?.dataset.title || '', e.currentTarget);
            });
        });
        // Wire "ingest into the open conversation" buttons
        listEl.querySelectorAll('.ingest-conv-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const item = e.target.closest('.audio-file-item');
                const filename = item?.dataset.filename;
                if (!filename) return;
                await ingestRecordingIntoConversation(filename, e.currentTarget);
            });
        });
        // Wire edit-speakers buttons (opens the rename dialog in edit mode)
        listEl.querySelectorAll('.edit-speakers-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const item = e.target.closest('.audio-file-item');
                const filename = item?.dataset.filename;
                if (!filename) return;
                // Pull the cached file record we rendered from so we can open
                // the dialog without a round-trip
                const fileRecord = filesToRender.find(ff => ff.filename === filename);
                if (!fileRecord) return;
                closeAudioManager();
                openSpeakerEditDialog(fileRecord);
            });
        });
        // Wire download buttons — direct browser download (no fetch blob)
        listEl.querySelectorAll('.download-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const item = btn.closest('.audio-file-item');
                const filename = item?.dataset.filename;
                const format = btn.dataset.format;
                if (!filename || !format) return;
                const token = getHeaders(false)['X-Auth-Token'] || '';
                const dlName = item?.dataset.downloadName || filename.replace(/\.[^.]+$/, '');
                const a = document.createElement('a');
                a.href = `${API_BASE}/import/audio/files/${encodeURIComponent(filename)}/download?format=${format}&token=${encodeURIComponent(token)}`;
                a.download = `${dlName}.${format}`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            });
        });

        // Wire merge selection — checkboxes on device/recovered recordings.
        // The merge bar shows when 2+ are checked; merging concatenates the
        // fragments oldest-first server-side (ffmpeg concat) into one new
        // server recording, then deletes the local fragments.
        wireMergeSelection(listEl);
    } catch (err) {
        listEl.innerHTML = '<div class="audio-file-empty">Failed to load files</div>';
        console.error('Audio file list error:', err);
    }
}

// ── Fragment Merge (crash recovery) ──────────────────────────────────────────

function wireMergeSelection(listEl) {
    const bar = document.getElementById('audio-merge-bar');
    const countEl = document.getElementById('audio-merge-count');
    let mergeBtn = document.getElementById('audio-merge-btn');
    if (!bar || !mergeBtn) return;

    const boxes = listEl.querySelectorAll('.merge-select');
    if (!boxes.length) {
        bar.hidden = true;
        return;
    }

    // Replace the button FIRST (drops click handlers stacked by previous
    // list refreshes), THEN bind everything to the live node. Ordering
    // matters: the first version of this code cloned the button AFTER
    // wiring update(), so every checkbox change updated a detached node
    // while the on-screen button stayed frozen in the disabled state it
    // was cloned with.
    const freshBtn = mergeBtn.cloneNode(true);
    mergeBtn.parentNode.replaceChild(freshBtn, mergeBtn);
    mergeBtn = freshBtn;

    const update = () => {
        const checked = listEl.querySelectorAll('.merge-select:checked');
        const n = checked.length;
        if (countEl) countEl.textContent = String(n);
        bar.hidden = n === 0;
        mergeBtn.disabled = n < 2;
        mergeBtn.textContent = n >= 2 ? `Merge ${n} recordings` : 'Merge';
    };

    boxes.forEach(box => box.addEventListener('change', update));
    update();

    const setMerging = (on) => {
        if (on) {
            mergeBtn.disabled = true;
            mergeBtn.innerHTML =
                '<svg class="audio-merge-spinner" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">'
                + '<circle cx="12" cy="12" r="9" stroke-opacity="0.25"/>'
                + '<path d="M21 12a9 9 0 0 0-9-9"/></svg> Merging…';
            boxes.forEach(b => { b.disabled = true; });
        } else {
            boxes.forEach(b => { b.disabled = false; });
            update();  // recomputes label + disabled from current selection
        }
    };

    mergeBtn.addEventListener('click', async () => {
        const checked = [...listEl.querySelectorAll('.merge-select:checked')];
        if (checked.length < 2) return;

        // Oldest first — "newer audio appended to the other in sequence"
        const selections = checked
            .map(box => ({
                recordingId: box.dataset.recordingId,
                created: parseFloat(box.dataset.created || '0'),
            }))
            .sort((a, b) => a.created - b.created);

        const confirmed = await confirmDialog({
            title: 'Merge Recordings',
            message: `Merge ${selections.length} recordings into one? They'll be joined oldest → newest. The original fragments are removed from this device after a successful merge.`,
            confirmText: 'Merge',
        });
        if (!confirmed) return;

        setMerging(true);
        try {
            // Assemble each fragment's chunks into a blob, oldest first
            const formData = new FormData();
            for (let i = 0; i < selections.length; i++) {
                const chunks = await getChunks(selections[i].recordingId);
                if (!chunks.length) throw new Error(`Fragment ${i + 1} has no audio data`);
                const blob = new Blob(chunks.map(c => c.blob), { type: 'audio/webm' });
                formData.append('files', blob, `fragment-${i}.webm`);
            }
            formData.append('title', `Merged Recording ${new Date().toLocaleDateString()}`);

            const res = await fetch(`${API_BASE}/import/audio/merge`, {
                method: 'POST',
                headers: getHeaders(false),
                body: formData,
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({ detail: 'Merge failed' }));
                throw new Error(err.detail || 'Merge failed');
            }
            await res.json();

            // Server has the merged file — remove the local fragments
            const saved = JSON.parse(localStorage.getItem('ragsmith_device_recordings') || '[]');
            const mergedIds = new Set(selections.map(s => s.recordingId));
            for (const id of mergedIds) {
                try { await clearChunks(id); } catch { /* best effort */ }
            }
            localStorage.setItem('ragsmith_device_recordings',
                JSON.stringify(saved.filter(r => !mergedIds.has(r.recordingId))));
            // If the recovery marker pointed at one of the merged fragments,
            // clear it — its chunks are gone.
            if (mergedIds.has(getActiveRecordingMarker())) clearActiveRecordingMarker();

            refreshAudioFileList();
        } catch (err) {
            console.error('Merge failed:', err);
            setMerging(false);
            await confirmDialog({
                title: 'Merge Failed',
                message: err.message || 'Could not merge the recordings. The fragments are untouched.',
                confirmText: 'OK',
            });
        }
        // No finally-reset on success: refreshAudioFileList() re-renders the
        // list and re-runs wireMergeSelection with a fresh button.
    });
}

// Last-loaded raw text, used by the Copy button
let _viewerRawText = '';

function parseTranscriptTurns(raw) {
    // Format: "[HH:MM:SS - HH:MM:SS] Speaker Label:\ntext...\n\n"
    // Returns a list of {startTs, endTs, speaker, text} turns. If no match, null.
    const turns = [];
    const re = /^\[(\d{2}:\d{2}:\d{2}) - (\d{2}:\d{2}:\d{2})\] ([^:\n]+):\s*$/gm;
    const matches = [...raw.matchAll(re)];
    if (!matches.length) return null;
    for (let i = 0; i < matches.length; i++) {
        const m = matches[i];
        const bodyStart = m.index + m[0].length;
        const bodyEnd = i + 1 < matches.length ? matches[i + 1].index : raw.length;
        const body = raw.slice(bodyStart, bodyEnd).trim();
        turns.push({ startTs: m[1], endTs: m[2], speaker: m[3].trim(), text: body });
    }
    return turns;
}

function renderTranscriptIntoViewer(raw) {
    const content = document.querySelector('#transcript-viewer .transcript-content');
    if (!content) return;
    content.classList.remove('summary-content', 'plain-content');
    content.innerHTML = '';

    const turns = parseTranscriptTurns(raw);
    if (!turns) {
        content.classList.add('plain-content');
        content.textContent = raw;
        return;
    }

    // Assign a color index per unique speaker (0-4, wraps)
    const speakerIdx = new Map();
    turns.forEach(t => {
        if (!speakerIdx.has(t.speaker)) speakerIdx.set(t.speaker, speakerIdx.size % 5);
    });

    turns.forEach(t => {
        const row = document.createElement('div');
        row.className = 'transcript-turn';
        const meta = document.createElement('div');
        meta.className = 'transcript-turn-meta';
        const speaker = document.createElement('span');
        speaker.className = 'transcript-turn-speaker';
        speaker.dataset.sidx = String(speakerIdx.get(t.speaker));
        speaker.textContent = t.speaker;
        const time = document.createElement('span');
        time.className = 'transcript-turn-time';
        time.textContent = `${t.startTs} → ${t.endTs}`;
        meta.appendChild(speaker);
        meta.appendChild(time);
        const body = document.createElement('div');
        body.className = 'transcript-turn-text';
        body.textContent = t.text;
        row.appendChild(meta);
        row.appendChild(body);
        content.appendChild(row);
    });
}

function renderSummaryIntoViewer(raw) {
    const content = document.querySelector('#transcript-viewer .transcript-content');
    if (!content) return;
    content.classList.remove('summary-content', 'plain-content');
    if (window.marked && window.DOMPurify) {
        content.classList.add('summary-content');
        const rendered = window.marked.parse(raw || '');
        content.innerHTML = window.DOMPurify.sanitize(rendered);
    } else {
        content.classList.add('plain-content');
        content.textContent = raw;
    }
}

function openTranscriptModal(titleText, kindLabel) {
    const viewer = document.getElementById('transcript-viewer');
    if (!viewer) return;
    const title = viewer.querySelector('.transcript-title');
    const kind = viewer.querySelector('.transcript-viewer-kind');
    if (title) title.textContent = titleText;
    if (kind) kind.textContent = kindLabel;
    // Reset the copy button state
    const copyBtn = document.getElementById('transcript-viewer-copy');
    const copyLabel = copyBtn?.querySelector('.transcript-viewer-copy-label');
    if (copyBtn) copyBtn.classList.remove('copied');
    if (copyLabel) copyLabel.textContent = 'Copy';
    viewer.classList.remove('hidden');
    viewer.classList.add('active');
}

/**
 * Open one of a recording's text parts by name.
 *
 * Exists so the chat card can reach the same viewers the recording manager
 * uses, without importing three functions or duplicating their fetch logic.
 * The manager does not have to be open.
 */
export async function openRecordingPart(filename, displayTitle, kind) {
    if (kind === 'transcript') return showTranscriptViewer(filename, displayTitle);
    if (kind === 'synopsis') return showSynopsisViewer(filename, displayTitle, null);
    return showSummaryViewer(filename, displayTitle);
}

async function showTranscriptViewer(filename, displayTitle) {
    try {
        const res = await fetch(`${API_BASE}/import/audio/files/${encodeURIComponent(filename)}/transcript`, {
            headers: getHeaders(false),
        });
        if (!res.ok) throw new Error('Failed to load transcript');
        const data = await res.json();
        _viewerRawText = data.transcript || '';
        renderTranscriptIntoViewer(_viewerRawText);
        openTranscriptModal(displayTitle || filename.replace(/\.[^.]+$/, ''), 'TRANSCRIPT');
    } catch (err) {
        console.error('Transcript load error:', err);
    }
}

async function showSummaryViewer(filename, displayTitle) {
    try {
        const res = await fetch(`${API_BASE}/import/audio/files/${encodeURIComponent(filename)}/summary`, {
            headers: getHeaders(false),
        });
        if (!res.ok) throw new Error('Failed to load summary');
        const data = await res.json();
        _viewerRawText = data.summary || '';
        renderSummaryIntoViewer(_viewerRawText);
        openTranscriptModal(displayTitle || filename.replace(/\.[^.]+$/, ''), 'NARRATIVE');
    } catch (err) {
        console.error('Summary load error:', err);
    }
}

/**
 * Show the brief summary, writing it first if it does not exist yet.
 *
 * Generating on first view rather than during processing keeps the cost off
 * every recording: most are never summarised, and the narrative they would be
 * summarised from is the expensive part that already ran.
 */
async function showSynopsisViewer(filename, displayTitle, btn) {
    const label = btn ? btn.textContent.trim() : '';
    try {
        let res = await fetch(`${API_BASE}/import/audio/files/${encodeURIComponent(filename)}/synopsis`, {
            headers: getHeaders(false),
        });
        if (res.status === 404) {
            // Not written yet. Say so on the button — the model call takes
            // real time and a button that just sits there reads as ignored.
            if (btn) { btn.disabled = true; btn.textContent = 'Summarizing…'; }
            res = await fetch(`${API_BASE}/import/audio/files/${encodeURIComponent(filename)}/synopsis`, {
                method: 'POST',
                headers: { ...getHeaders(false), 'Content-Type': 'application/json' },
                body: JSON.stringify({}),
            });
        }
        if (!res.ok) {
            let detail = `Summary failed (${res.status})`;
            try { const b = await res.json(); if (b.detail) detail = b.detail; } catch (_) {}
            throw new Error(detail);
        }
        const data = await res.json();
        _viewerRawText = data.synopsis || '';
        renderSummaryIntoViewer(_viewerRawText);
        openTranscriptModal(displayTitle || filename.replace(/\.[^.]+$/, ''), 'SUMMARY');
    } catch (err) {
        console.error('Summary error:', err);
        showToast(err.message || 'Could not produce a summary', 'error');
    } finally {
        if (btn) { btn.disabled = false; btn.textContent = label || 'Summary'; }
    }
}

/**
 * Put a recording into the open conversation's memory.
 *
 * Closes the manager on success and drops a card into the chat, because the
 * result of this action lives in the conversation — leaving the operator in
 * a file list, with a toast, hides the thing they just made.
 */
async function ingestRecordingIntoConversation(filename, btn) {
    const sessionId = state.sessionId;
    if (!sessionId) {
        showToast('Open a conversation first', 'error');
        return;
    }
    const label = btn ? btn.textContent.trim() : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Ingesting…'; }
    try {
        const res = await fetch(
            `${API_BASE}/import/audio/files/${encodeURIComponent(filename)}/ingest-conversation`,
            {
                method: 'POST',
                headers: { ...getHeaders(false), 'Content-Type': 'application/json' },
                body: JSON.stringify({ session_id: sessionId, model: state.loadedModel || state.selectedModel || '' }),
            }
        );
        if (!res.ok) {
            let detail = `Ingest failed (${res.status})`;
            try { const b = await res.json(); if (b.detail) detail = b.detail; } catch (_) {}
            throw new Error(detail);
        }
        const card = await res.json();
        closeAudioManager();
        const { renderRecordingIngestCard } = await import('./ui.js');
        renderRecordingIngestCard(card, { sessionId });
    } catch (err) {
        console.error('Recording ingest error:', err);
        showToast(err.message || 'Could not ingest that recording', 'error');
    } finally {
        if (btn) { btn.disabled = false; btn.textContent = label || 'Ingest'; }
    }
}

async function copyViewerText() {
    const btn = document.getElementById('transcript-viewer-copy');
    const label = btn?.querySelector('.transcript-viewer-copy-label');
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(_viewerRawText || '');
        } else {
            // Fallback for non-secure contexts / older browsers
            const ta = document.createElement('textarea');
            ta.value = _viewerRawText || '';
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
        }
        if (btn) btn.classList.add('copied');
        if (label) label.textContent = 'Copied!';
        setTimeout(() => {
            if (btn) btn.classList.remove('copied');
            if (label) label.textContent = 'Copy';
        }, 1800);
    } catch (err) {
        console.error('Copy failed:', err);
        if (label) label.textContent = 'Copy failed';
        setTimeout(() => { if (label) label.textContent = 'Copy'; }, 1800);
    }
}

function closeTranscriptViewer() {
    const viewer = document.getElementById('transcript-viewer');
    if (viewer) {
        viewer.classList.remove('active');
        viewer.classList.add('hidden');
    }
}

function processExistingFile(filePath, savedTitle, existing) {
    lastUploadedPath = filePath;
    showPreProcessDialog(filePath, savedTitle, existing);
}

function showPreProcessDialog(filePath, savedTitle, existing) {
    const dialog = document.getElementById('recording-preprocess-dialog');
    if (!dialog) return;

    // Use saved title if provided, otherwise derive from filename
    let defaultName;
    if (savedTitle) {
        defaultName = savedTitle;
    } else {
        const stem = (filePath || '').split('/').pop().replace(/\.[^.]+$/, '').replace(/[_]/g, '-');
        defaultName = stem || `recording-${new Date().toISOString().slice(0, 10)}`;
    }

    const input = document.getElementById('rec-preprocess-kb-input');
    if (input) input.value = defaultName;

    dialog.classList.add('active');

    // Wire chip toggles with mutual exclusivity
    const wavChip = document.getElementById('rec-chip-wav');
    const mp3Chip = document.getElementById('rec-chip-mp3');
    const uploadOnlyChip = document.getElementById('rec-chip-upload-only');
    const deviceOnlyChip = document.getElementById('rec-chip-device-only');
    const transcriptChip0 = document.getElementById('rec-chip-transcript');
    const summaryChip0 = document.getElementById('rec-chip-summary');
    const diarizationChip0 = document.getElementById('rec-chip-diarization');
    const kbChip0 = document.getElementById('rec-chip-kb');
    const synopsisChip0 = document.getElementById('rec-chip-synopsis');
    const denoiseChip0 = document.getElementById('rec-chip-denoise');

    // Reset chip state to defaults
    wavChip?.classList.remove('active');
    mp3Chip?.classList.remove('active');
    uploadOnlyChip?.classList.remove('active');
    deviceOnlyChip?.classList.remove('active');
    // Processing chips: transcript + summary default ON for fresh, but OFF when
    // the file already has that output (prevents accidental overwrite on reprocess).
    // Diarization + KB always default OFF.
    const hasTranscriptAlready = existing?.hasTranscript === true;
    const hasSummaryAlready = existing?.hasSummary === true;
    transcriptChip0?.classList.toggle('active', !hasTranscriptAlready);
    summaryChip0?.classList.toggle('active', !hasSummaryAlready);
    // The brief summary is opt-in, always: it costs another model pass, and
    // most recordings are processed for their transcript and narrative alone.
    synopsisChip0?.classList.remove('active');
    diarizationChip0?.classList.remove('active');
    kbChip0?.classList.remove('active');
    // Default "Reduce Noise" ON when Capture Room Audio is in use — those
    // captures (e.g. a Bluetooth earbud mic) are noisy and quiet, and need the
    // cleanup + loudness boost to transcribe and be listenable. Still toggleable.
    denoiseChip0?.classList.toggle(
        'active', localStorage.getItem('ragsmith_capture_room_audio') === '1'
    );

    // Remove old listeners by cloning
    [wavChip, mp3Chip, uploadOnlyChip, deviceOnlyChip, transcriptChip0, summaryChip0, synopsisChip0, diarizationChip0, kbChip0, denoiseChip0].forEach(chip => {
        if (!chip) return;
        const fresh = chip.cloneNode(true);
        chip.parentNode.replaceChild(fresh, chip);
    });

    // Re-grab after cloning
    const wav = document.getElementById('rec-chip-wav');
    const mp3 = document.getElementById('rec-chip-mp3');
    const uploadOnly = document.getElementById('rec-chip-upload-only');
    const deviceOnly = document.getElementById('rec-chip-device-only');
    const transcriptChip = document.getElementById('rec-chip-transcript');
    const summaryChip = document.getElementById('rec-chip-summary');
    const synopsisChip = document.getElementById('rec-chip-synopsis');
    const diarizationChip = document.getElementById('rec-chip-diarization');
    const kbChip = document.getElementById('rec-chip-kb');
    const denoiseChip = document.getElementById('rec-chip-denoise');

    // Independent toggles for processing chips (no mutual exclusivity with each other)
    [transcriptChip, summaryChip, synopsisChip, diarizationChip, kbChip, denoiseChip].forEach(chip => {
        chip?.addEventListener('click', () => chip.classList.toggle('active'));
    });

    // Summary depends on Narrative: it is written from it. Turning Narrative
    // off drags Summary off with it and greys it, rather than letting someone
    // ask for a summary of something that will not exist.
    const syncSynopsisChip = () => {
        if (!synopsisChip) return;
        const canSummarise = summaryChip?.classList.contains('active');
        if (!canSummarise) synopsisChip.classList.remove('active');
        synopsisChip.classList.toggle('chip-muted', !canSummarise);
        synopsisChip.title = canSummarise
            ? 'Also write a brief summary from the narrative'
            : 'Needs Narrative — the summary is written from it';
    };
    summaryChip?.addEventListener('click', syncSynopsisChip);
    syncSynopsisChip();
    if (diarizationChip) {
        diarizationChip.title = 'Label speakers (silently skipped if no HuggingFace token is configured)';
    }

    // When Upload Only or Device Only is toggled on, visually mute the processing chips
    // (they won't run in those modes regardless). Don't disable the chips — user can still preview state.
    const updateProcessingChipMute = () => {
        const bypass = uploadOnly?.classList.contains('active') || deviceOnly?.classList.contains('active');
        [transcriptChip, summaryChip, synopsisChip, diarizationChip, kbChip].forEach(c => {
            if (!c) return;
            c.classList.toggle('chip-muted', bypass);
        });
    };

    // Server-side chips (wav, mp3, upload only) — mutually exclusive with device only
    uploadOnly?.addEventListener('click', () => {
        const activating = !uploadOnly.classList.contains('active');
        uploadOnly.classList.toggle('active', activating);
        if (activating) {
            wav?.classList.remove('active');
            mp3?.classList.remove('active');
            deviceOnly?.classList.remove('active');
        }
        updateProcessingChipMute();
    });

    [wav, mp3].forEach(chip => {
        chip?.addEventListener('click', () => {
            chip.classList.toggle('active');
            if (wav?.classList.contains('active') || mp3?.classList.contains('active')) {
                uploadOnly?.classList.remove('active');
                deviceOnly?.classList.remove('active');
            }
            updateProcessingChipMute();
        });
    });

    // Device only — mutually exclusive with all others
    deviceOnly?.addEventListener('click', () => {
        const activating = !deviceOnly.classList.contains('active');
        deviceOnly.classList.toggle('active', activating);
        if (activating) {
            wav?.classList.remove('active');
            mp3?.classList.remove('active');
            uploadOnly?.classList.remove('active');
        }
        updateProcessingChipMute();
    });

    updateProcessingChipMute();

    // Wire buttons (remove old listeners by cloning)
    const startBtn = document.getElementById('rec-preprocess-start');
    const cancelBtn = document.getElementById('rec-preprocess-cancel');

    const newStart = startBtn.cloneNode(true);
    startBtn.parentNode.replaceChild(newStart, startBtn);
    const newCancel = cancelBtn.cloneNode(true);
    cancelBtn.parentNode.replaceChild(newCancel, cancelBtn);

    newCancel.addEventListener('click', () => {
        dialog.classList.remove('active');
    });

    newStart.addEventListener('click', async () => {
        const enableTranscript = document.getElementById('rec-chip-transcript')?.classList.contains('active') ?? true;
        const enableSummary = document.getElementById('rec-chip-summary')?.classList.contains('active') ?? true;
        // The brief summary is written FROM the narrative, so it cannot be
        // produced without one. Rather than fail late in the pipeline, the
        // chip is forced off here and disabled in the UI when Narrative is off.
        const enableSynopsis = enableSummary
            && (document.getElementById('rec-chip-synopsis')?.classList.contains('active') || false);
        const enableDiarization = document.getElementById('rec-chip-diarization')?.classList.contains('active') ?? true;
        const enableKb = document.getElementById('rec-chip-kb')?.classList.contains('active') ?? true;
        const exportWav = document.getElementById('rec-chip-wav')?.classList.contains('active') || false;
        const exportMp3 = document.getElementById('rec-chip-mp3')?.classList.contains('active') || false;
        const denoise = document.getElementById('rec-chip-denoise')?.classList.contains('active') || false;
        const isUploadOnly = document.getElementById('rec-chip-upload-only')?.classList.contains('active') || false;
        const isDeviceOnly = document.getElementById('rec-chip-device-only')?.classList.contains('active') || false;

        let kbName = document.getElementById('rec-preprocess-kb-input')?.value.trim() || '';
        // KB name required for KB ingestion or device-only save (those need a filename).
        // For processing-only (no KB), name is optional — used as the title.
        if (!kbName && (enableKb || isDeviceOnly)) {
            showError(enableKb ? 'Enter a Knowledge Base name or turn off "Add to new KB".' : 'Enter a name for the device recording.');
            return;
        }

        if (isDeviceOnly) {
            dialog.classList.remove('active');
            await saveToDeviceOnly(kbName);
            return;
        }

        const hfToken = localStorage.getItem('ragsmith_hf_token') || '';

        if (isUploadOnly) {
            dialog.classList.remove('active');
            uploadOnlyFlow(kbName, hfToken, filePath);
            return;
        }

        // Summary requires a loaded model. Transcript-only / diarization-only don't.
        if (enableSummary && !state.loadedModel) {
            showError('Load a model before processing with Summary enabled, or turn Summary off.');
            return;
        }

        dialog.classList.remove('active');

        const procOpts = { enableTranscript, enableSummary, enableSynopsis, enableDiarization, enableKb, denoise };

        if (filePath) {
            lastUploadedPath = filePath;
            lastKbName = kbName;
            lastHfToken = hfToken;
            lastProcArgs = { exportWav, exportMp3, procOpts };

            setRecordingState('processing');
            const progressText = document.getElementById('rec-progress-text');
            const progressBar = document.getElementById('rec-progress-bar');
            if (progressText) progressText.textContent = 'Processing...';
            if (progressBar) progressBar.style.width = '0%';

            processAudioSSE(filePath, kbName, hfToken, exportWav, exportMp3, procOpts).catch(e => {
                console.error('Processing failed:', e);
                showError(e.message);
            });
        } else {
            processRecording(kbName, hfToken, exportWav, exportMp3, procOpts);
        }
    });
}

// ── Initialization ───────────────────────────────────────────────────────────

export function initAudioRecorder() {
    // Show Share Audio toggle if browser supports getDisplayMedia with audio
    // Firefox doesn't support audio capture via getDisplayMedia, so check for Chromium
    const isChromium = /Chrome|Chromium|Edg/.test(navigator.userAgent);
    if (isChromium && navigator.mediaDevices?.getDisplayMedia) {
        const toggle = document.getElementById('share-audio-toggle');
        if (toggle) toggle.style.display = '';
    }

    // Capture Room Audio toggle — persist across reloads
    const roomAudioCheckbox = document.getElementById('rec-room-audio');
    if (roomAudioCheckbox) {
        roomAudioCheckbox.checked = localStorage.getItem('ragsmith_capture_room_audio') === '1';
        roomAudioCheckbox.addEventListener('change', () => {
            localStorage.setItem('ragsmith_capture_room_audio', roomAudioCheckbox.checked ? '1' : '0');
        });
    }

    // Start Muted toggle — persist across reloads
    const startMutedCheckbox = document.getElementById('rec-start-muted');
    if (startMutedCheckbox) {
        startMutedCheckbox.checked = localStorage.getItem('ragsmith_start_muted') === '1';
        startMutedCheckbox.addEventListener('change', () => {
            localStorage.setItem('ragsmith_start_muted', startMutedCheckbox.checked ? '1' : '0');
        });
    }

    // Auto-stop on Silence toggle — persist; default ON
    const silenceStopCheckbox = document.getElementById('rec-silence-stop');
    if (silenceStopCheckbox) {
        const stored = localStorage.getItem('ragsmith_silence_stop');
        silenceStopCheckbox.checked = stored === null ? true : stored === '1';
        silenceStopCheckbox.addEventListener('change', () => {
            localStorage.setItem('ragsmith_silence_stop', silenceStopCheckbox.checked ? '1' : '0');
        });
    }

    // Mute button
    document.getElementById('rec-mute-btn')?.addEventListener('click', toggleMute);

    // Pause/resume button
    document.getElementById('rec-pause-btn')?.addEventListener('click', togglePause);

    // Stop button
    document.getElementById('rec-stop-btn')?.addEventListener('click', stopRecording);

    // Auto-upload countdown buttons
    document.getElementById('rec-autoupload-cancel')?.addEventListener('click', cancelAutoUploadCountdown);
    document.getElementById('rec-autoupload-now')?.addEventListener('click', triggerAutoUpload);

    // Complete overlay buttons
    document.getElementById('rec-preview-btn')?.addEventListener('click', previewRecording);
    document.getElementById('rec-discard-btn')?.addEventListener('click', discardRecording);
    document.getElementById('rec-process-btn')?.addEventListener('click', () => {
        // Show pre-process dialog to get KB name before processing
        showPreProcessDialog(null); // null = will use recordingId chunks (fresh recording)
    });

    // Error recovery buttons
    document.getElementById('rec-retry-btn')?.addEventListener('click', retryProcessing);
    document.getElementById('rec-dismiss-btn')?.addEventListener('click', dismissProcessing);
    document.getElementById('rec-stop-processing-btn')?.addEventListener('click', stopProcessing);

    // KB dialog buttons
    document.getElementById('rec-kb-confirm')?.addEventListener('click', confirmKBImport);
    document.getElementById('rec-kb-cancel')?.addEventListener('click', cancelKBImport);
    document.getElementById('rec-kb-ref-transcript')?.addEventListener('click', () => {
        const ctx = _speakerDialogContext;
        if (!ctx?.filename) return;
        showTranscriptViewer(ctx.filename, ctx.title || '');
    });
    document.getElementById('rec-kb-ref-summary')?.addEventListener('click', () => {
        const ctx = _speakerDialogContext;
        if (!ctx?.filename) return;
        showSummaryViewer(ctx.filename, ctx.title || '');
    });

    // Audio Manager modal buttons
    document.getElementById('audio-manager-close')?.addEventListener('click', closeAudioManager);
    document.getElementById('audio-start-rec-btn')?.addEventListener('click', () => {
        closeAudioManager();
        startRecording().catch(err => {
            console.error('Recording failed:', err);
            let msg = 'Recording failed: ' + (err.message || err);
            if (err.name === 'NotFoundError') {
                msg = 'No microphone found. Check that a mic is connected and not in use by another app.';
            } else if (err.name === 'NotAllowedError') {
                msg = 'Microphone access denied. Allow mic permission for this site and try again.';
            } else if (err.name === 'NotReadableError') {
                msg = 'Microphone is in use by another app. Close it and try again.';
            }
            alert(msg);
        });
    });

    // Import audio file
    const importBtn = document.getElementById('audio-import-btn');
    const importInput = document.getElementById('audio-import-input');
    if (importBtn && importInput) {
        importBtn.addEventListener('click', () => importInput.click());
        importInput.addEventListener('change', async () => {
            const file = importInput.files?.[0];
            if (!file) return;
            importBtn.disabled = true;
            importBtn.textContent = 'Uploading...';
            try {
                const formData = new FormData();
                formData.append('file', file, file.name);
                const res = await fetch(`${API_BASE}/import/audio/upload`, {
                    method: 'POST',
                    headers: getHeaders(false),
                    body: formData,
                });
                if (!res.ok) throw new Error('Upload failed');
                await refreshAudioFileList();
            } catch (err) {
                console.error('Import failed:', err);
            } finally {
                importBtn.disabled = false;
                importBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right:6px"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg> Import Audio`;
                importInput.value = '';
            }
        });
    }

    // Close modal on backdrop click
    document.getElementById('audio-manager-modal')?.addEventListener('click', (e) => {
        if (e.target.id === 'audio-manager-modal') closeAudioManager();
    });

    // Transcript viewer
    document.getElementById('transcript-viewer-close')?.addEventListener('click', closeTranscriptViewer);
    document.getElementById('transcript-viewer-copy')?.addEventListener('click', copyViewerText);
    document.getElementById('transcript-viewer')?.addEventListener('click', (e) => {
        if (e.target.id === 'transcript-viewer') closeTranscriptViewer();
    });

    // Check for incomplete recordings on load
    checkForIncompleteRecording();

    checkForWaitingNames();
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkForWaitingNames();
    });
}
